import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { supabase } from '../supabaseClient'
import ClientePDFs from './ClientePDFs'
import { enviarEmailBaixaVistoria } from '../services/vistoriaBaixaEmail'
import './GestaoInstalacao.css'

/** Administrativo desta empresa visualiza ordens de todas as empresas (como administrador); demais perfis seguem a lógica atual. */
const EMPRESA_ID_ADMINISTRATIVO_VER_TODAS_OS = 'f0c6de5a-eaf9-4530-a6a7-28140091897d'

/**
 * Indica se o tipo de serviço exige o campo "Qtd de Módulos" no Formulário de Baixa.
 * Aplica-se a: Desinstalação, Desinstalação/Reinstalação, Reinstalação,
 * Retirada de Painéis e Retirada de Painéis + Inversor.
 */
const tipoExigeQuantidadeModulos = (nomeTipo) => {
  const nome = (nomeTipo || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos para comparação robusta
  const tiposComModulos = [
    'desinstalacao',
    'desinstalacao/reinstalacao',
    'reinstalacao',
    'retirada de paineis',
    'retirada de paineis + inversor',
  ]
  return tiposComModulos.includes(nome)
}

/** Aplica máscara de moeda (##,##) à medida que o usuário digita, tratando a entrada como centavos. */
const aplicarMascaraValor = (valor) => {
  const digitos = (valor == null ? '' : String(valor)).replace(/\D/g, '')
  if (!digitos) return ''
  const numero = parseInt(digitos, 10) / 100
  return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Formata um número (ex.: vindo do banco) na máscara de moeda ##,##. */
const formatarValorParaMascara = (valor) => {
  if (valor == null || valor === '') return ''
  const numero = Number(valor)
  if (isNaN(numero)) return ''
  return numero.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** Converte um valor mascarado (ex.: "1.500,50") em número. Retorna null se inválido. */
const parseValorMascarado = (valor) => {
  if (valor == null || valor.toString().trim() === '') return null
  const limpo = valor.toString().replace(/\./g, '').replace(',', '.')
  const numero = parseFloat(limpo)
  return isNaN(numero) ? null : numero
}

/** Rótulos das fotos da baixa (usados na visualização e no e-mail de confirmação). */
const LABELS_FOTOS_BAIXA = {
  foto_planta_monitoramento: 'Foto da Planta de Monitoramento',
  foto_fachada: 'Foto da Fachada',
  foto_padrao_completo: 'Foto do Padrão Completo',
  foto_etiqueta_inversor: 'Foto da Etiqueta do Inversor',
  foto_datalogger: 'Foto do Datalogger',
  foto_instalacao_inversor: 'Foto da Instalação do Inversor',
  foto_qpca: 'Foto do QPCA',
  foto_disjuntor_padrao: 'Foto do Disjuntor do Padrão',
  foto_aterramento_padrao: 'Foto do Aterramento do Padrão',
  foto_conexoes_padrao: 'Foto das Conexões do Padrão',
  foto_disjuntor_qpca: 'Foto do Disjuntor do QPCA',
  foto_aterramento_qpca: 'Foto do Aterramento do QPCA',
  foto_medicoes_strings: 'Foto das Medições das Strings',
  foto_antes_modulos: 'Foto Antes de Instalar os Módulos',
  foto_apos_modulos: 'Foto Após Instalar os Módulos',
  foto_strings: 'Foto das Strings',
  foto_antes_manutencao_correcao: 'Foto Antes do Serviço',
  foto_apos_manutencao_correcao: 'Foto Após o Serviço',
}

/** Converte o objeto de fotos da baixa em [{ titulo, urls }] para o e-mail de confirmação. */
const montarFotosBaixaEmail = (fotosObj) => {
  if (!fotosObj || typeof fotosObj !== 'object') return []
  const grupos = []
  Object.entries(fotosObj).forEach(([campo, valor]) => {
    const urls = (Array.isArray(valor) ? valor : (valor ? [valor] : []))
      .map((u) => (typeof u === 'string' ? u : u?.url))
      .filter(Boolean)
    if (urls.length > 0) {
      grupos.push({ titulo: LABELS_FOTOS_BAIXA[campo] || campo.replace(/_/g, ' '), urls })
    }
  })
  return grupos
}

const GestaoInstalacao = ({ onBack, userRole, userEmpresa, userId }) => {
  // Função para formatar data sem hora (evita problemas de timezone)
  const formatarDataSemHora = (dataString) => {
    if (!dataString) return 'N/A'
    
    try {
      if (typeof dataString === 'string') {
        const dataLimpa = dataString.split('T')[0].trim()
        const partes = dataLimpa.split('-')
        
        if (partes.length === 3) {
          const [ano, mes, dia] = partes
          if (ano && mes && dia && !isNaN(ano) && !isNaN(mes) && !isNaN(dia)) {
            return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`
          }
        }
      }
      
      if (dataString instanceof Date) {
        const ano = dataString.getFullYear()
        const mes = String(dataString.getMonth() + 1).padStart(2, '0')
        const dia = String(dataString.getDate()).padStart(2, '0')
        return `${dia}/${mes}/${ano}`
      }
      
      const dataStr = String(dataString)
      if (dataStr.includes('-')) {
        const partes = dataStr.split('T')[0].split('-')
        if (partes.length === 3) {
          const [ano, mes, dia] = partes
          if (ano && mes && dia) {
            return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`
          }
        }
      }
    } catch (error) {
      console.warn('Erro ao formatar data:', error, dataString)
    }
    
    return 'N/A'
  }
  
  // Estados
  const [ordensServico, setOrdensServico] = useState([])
  const [isLoadingOrdens, setIsLoadingOrdens] = useState(true)
  // Opções de status para o filtro (multiseleção)
  const STATUS_OPCOES = ['Novo', 'Em andamento', 'Finalizado', 'Validado', 'Cancelado']
  const [filtros, setFiltros] = useState({
    cliente: '',
    status: ['Novo', 'Em andamento'], // padrão: Novo e Em andamento
    dataInicio: '',
    dataFim: ''
  })
  const [showStatusDropdown, setShowStatusDropdown] = useState(false)
  const [showFiltros, setShowFiltros] = useState(false)
  const statusTodosCheckRef = useRef(null)
  const [idTiposGestao, setIdTiposGestao] = useState([]) // Instalação, Manutenção, Corretiva, Manutenção Corretiva, Troca de Inversor, Retorno
  
  // Estados para modal de presença
  const [showPresencaModal, setShowPresencaModal] = useState(false)
  const [ordemSelecionada, setOrdemSelecionada] = useState(null)
  const [presencaFormData, setPresencaFormData] = useState({
    data_presenca: '',
    data_cadastro_preenchido: '',
    equipe_id: '',
    veiculo_id: '',
    km_inicial: '',
    observacoes: '',
    colaboradores: []
  })
  
  // Estados para dados do formulário
  const [equipes, setEquipes] = useState([])
  const [veiculos, setVeiculos] = useState([])
  const [colaboradores, setColaboradores] = useState([])
  const [isLoadingPresenca, setIsLoadingPresenca] = useState(false)
  
  // Estados para modal de confirmação de início de serviço
  const [showConfirmInicioModal, setShowConfirmInicioModal] = useState(false)
  const [ordemParaIniciar, setOrdemParaIniciar] = useState(null)
  const [isLoadingInicio, setIsLoadingInicio] = useState(false)
  
  // Estados para modal de sucesso
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  
  // Estados para modal de baixa de instalação
  const [showBaixaModal, setShowBaixaModal] = useState(false)
  const [ordemParaBaixa, setOrdemParaBaixa] = useState(null)
  const [isLoadingBaixa, setIsLoadingBaixa] = useState(false)
  
  // Estados para modal de visualização de baixa
  const [showVisualizarBaixaModal, setShowVisualizarBaixaModal] = useState(false)
  const [ordemParaVisualizar, setOrdemParaVisualizar] = useState(null)
  const [dadosBaixa, setDadosBaixa] = useState(null)
  const [isLoadingVisualizarBaixa, setIsLoadingVisualizarBaixa] = useState(false)
  const [isExcluindoBaixa, setIsExcluindoBaixa] = useState(false)
  
  // Estados para modal de confirmação de exclusão
  const [showConfirmExcluirBaixaModal, setShowConfirmExcluirBaixaModal] = useState(false)
  // ID do formulário de baixa existente quando editando ordem Finalizado
  const [baixaExistenteId, setBaixaExistenteId] = useState(null)
  const [isLoadingBaixaParaEdicao, setIsLoadingBaixaParaEdicao] = useState(false)
  // Estados para modal de PDFs anexados (cliente da ordem)
  const [showPdfsModal, setShowPdfsModal] = useState(false)
  const [pdfsModalData, setPdfsModalData] = useState(null)
  // Modal ao clicar no nome do cliente: opções 1) Info endereço/kit/fachada/mapa  2) Baixar/Editar ou Consultar
  const [showClienteOpcoesModal, setShowClienteOpcoesModal] = useState(false)
  const [clienteOpcoesOrdem, setClienteOpcoesOrdem] = useState(null)
  const [tiposPadrao, setTiposPadrao] = useState([])
  const [baixaFormData, setBaixaFormData] = useState({
    tipo_formulario: 'Instalação',
    tipo_padrao_id: '',
    tipo_fase: '', // Monofásico, Bifásico, Trifásico
    tipo_inversor: '', // Bifásico, Trifásico
    // Fotos obrigatórias sempre
    foto_fachada: [],
    foto_padrao_completo: [],
    // Fotos do padrão (condicionais por tipo_fase)
    foto_fase1_neutro: [],
    foto_terra_neutro: [],
    foto_fase1_fase2: [],
    foto_fase2_neutro: [],
    foto_fase2_fase3: [],
    foto_fase3_fase1: [],
    foto_fase3_neutro: [],
    // Fotos do padrão (sempre)
    foto_disjuntor_padrao: [],
    foto_aterramento_padrao: [],
    foto_conexoes_padrao: [], // múltiplas
    // Fotos do inversor (sempre)
    foto_instalacao_inversor: [],
    foto_qpca: [],
    foto_disjuntor_qpca: [],
    foto_aterramento_qpca: [],
    foto_medicoes_strings: [], // múltiplas
    foto_planta_monitoramento: [],
    foto_etiqueta_inversor: [],
    foto_datalogger: [],
    // Fotos do QPCA (condicionais por tipo_inversor)
    foto_fase1_terra_qpca: [],
    foto_fase2_terra_qpca: [],
    foto_fase3_terra_qpca: [],
    foto_fase1_fase2_qpca: [],
    foto_fase2_fase3_qpca: [],
    foto_fase3_fase1_qpca: [],
    // Fotos dos módulos
    foto_antes_modulos: [],
    foto_apos_modulos: [],
    foto_strings: [], // múltiplas
    foto_aterramento_modulos: [], // opcional
    foto_aterramento_trilhos: [], // opcional
    foto_antes_manutencao_correcao: [], // para Manutenção/Corretiva/Troca Inversor
    foto_apos_manutencao_correcao: [], // para Manutenção/Corretiva/Troca Inversor
    observacoes: '',
    // Novos campos
    quantidade_modulos: '',
    configuracao_inversor: false,
    nota_material: false,
    valor_material: '',
    valor_obra: ''
  })
  const [fotosPreview, setFotosPreview] = useState({})

  // Carregar tipo de serviço "Instalação" e ordens
  useEffect(() => {
    loadTipoInstalacao()
    loadEquipes()
    loadVeiculos()
    loadColaboradores()
    loadTiposPadrao()
  }, [])

  // Carregar tipos de padrão
  const loadTiposPadrao = async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_padrao')
        .select('id, nome')
        .order('nome', { ascending: true })

      if (error) {
        console.error('Erro ao carregar tipos de padrão:', error)
      } else {
        setTiposPadrao(data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar tipos de padrão:', error)
    }
  }

  useEffect(() => {
    if (idTiposGestao && idTiposGestao.length > 0) {
      loadOrdensServico()
    }
  }, [idTiposGestao])

  // Buscar IDs de TODOS os tipos de serviço criados, para que toda ordem de
  // serviço apareça na Gestão Instalação e possa ser baixada. A baixa de qualquer
  // tipo diferente de "Instalação" usa o formulário de foto antes/depois
  // (mesmo padrão de "Obra Cívil").
  const loadTipoInstalacao = async () => {
    try {
      const { data, error } = await supabase
        .from('tipo_servico')
        .select('id, nome')
        .order('nome')

      if (error) {
        console.error('Erro ao buscar tipos de serviço:', error)
        return
      }

      if (data && data.length > 0) {
        // Ignorar somente o tipo "Vistoria" (fluxo gerenciado em outra tela).
        const tiposGestao = data.filter((t) => (t.nome || '').trim().toLowerCase() !== 'vistoria')
        const ids = tiposGestao.map((t) => t.id)
        console.log('✅ Tipos de serviço para Gestão Instalação:', tiposGestao.map((t) => t.nome).join(', '))
        setIdTiposGestao(ids)
      } else {
        console.warn('⚠️ Nenhum tipo de serviço encontrado na tabela tipo_servico.')
        setIdTiposGestao([])
      }
    } catch (error) {
      console.error('Erro ao buscar tipos de serviço:', error)
    }
  }

  // Função para carregar ordens de serviço do tipo Instalação
  const loadOrdensServico = async () => {
    try {
      setIsLoadingOrdens(true)
      
      if (!idTiposGestao || idTiposGestao.length === 0) {
        console.warn('⚠️ Nenhum tipo de serviço (Instalação, Manutenção, Corretiva, Manutenção Corretiva, Troca de Inversor, Retorno, Obra Cívil) encontrado.')
        setOrdensServico([])
        setIsLoadingOrdens(false)
        return
      }

      console.log('🔍 Buscando ordens de serviço dos tipos:', idTiposGestao.length, 'tipos')
      
      let ordensData = []
      
      // Administrador, ou administrativo da empresa acima: todas as ordens (tipos de gestão). Demais perfis: mesma lógica de antes.
      const verTodasOrdensGestao =
        userRole === 'administrador' ||
        (userRole === 'administrativo' && userEmpresa?.id === EMPRESA_ID_ADMINISTRATIVO_VER_TODAS_OS)

      if (verTodasOrdensGestao) {
        const { data, error } = await supabase
          .from('ordens_servicos')
          .select(`
            id,
            id_cliente,
            id_tipo_servico,
            id_equipe,
            id_empresa,
            status,
            obs_operacional,
            obs_campo,
            data_execucao,
            data_criacao,
            created_at,
            updated_at,
            presenca
          `)
          .in('id_tipo_servico', idTiposGestao)
          .order('created_at', { ascending: false })
        
        if (error) {
          console.error('❌ Erro ao buscar ordens de serviço:', error)
          throw error
        }
        
        ordensData = data || []
        console.log('📋 Ordens encontradas (visão ampla):', ordensData.length)
      } else if (userRole === 'instalador' && userId) {
        // Se for instalador, buscar apenas ordens associadas ao usuário com status Novo e Em andamento
        const { data, error } = await supabase
          .from('ordens_servicos')
          .select(`
            id,
            id_cliente,
            id_tipo_servico,
            id_equipe,
            id_empresa,
            status,
            obs_operacional,
            obs_campo,
            data_execucao,
            data_criacao,
            created_at,
            updated_at,
            presenca,
            ordem_servico_responsaveis!inner(
              id,
              id_responsavel,
              id_ordem_servico
            )
          `)
          .in('id_tipo_servico', idTiposGestao)
          .eq('ordem_servico_responsaveis.id_responsavel', userId)
          .in('status', ['Novo', 'Em andamento'])
          .order('created_at', { ascending: false })
        
        if (error) {
          console.error('❌ Erro ao buscar ordens de instalação do instalador:', error)
          throw error
        }
        
        ordensData = data || []
        console.log('📋 Ordens de instalação encontradas (instalador):', ordensData.length)
      } else {
        // Se não for administrador, buscar ordens da empresa do usuário
        // e também ordens que a empresa tem permissão de ver
        if (userEmpresa?.id) {
          // 1. Buscar ordens da empresa do usuário
          const { data: ordensEmpresa, error: errorEmpresa } = await supabase
            .from('ordens_servicos')
            .select(`
              id,
              id_cliente,
              id_tipo_servico,
              id_equipe,
              id_empresa,
              status,
              obs_operacional,
              obs_campo,
              data_execucao,
              data_criacao,
              created_at,
              updated_at,
              presenca
            `)
            .in('id_tipo_servico', idTiposGestao)
            .eq('id_empresa', userEmpresa.id)
            .order('created_at', { ascending: false })
          
          if (errorEmpresa) {
            console.error('❌ Erro ao buscar ordens da empresa:', errorEmpresa)
            throw errorEmpresa
          }
          
          const ordensEmpresaList = ordensEmpresa || []
          console.log('📋 Ordens da empresa do usuário:', ordensEmpresaList.length)
          
          // 2. Buscar permissões de instalações autorizadas
          const { data: permissoes, error: errorPermissoes } = await supabase
            .from('permissoes_instalacoes')
            .select('instalacao_id')
            .eq('empresa_autorizada_id', userEmpresa.id)
          
          if (errorPermissoes) {
            console.error('❌ Erro ao buscar permissões:', errorPermissoes)
            // Não lançar erro, apenas continuar sem permissões adicionais
          }
          
          const instalacaoIdsPermitidos = (permissoes || []).map(p => p.instalacao_id)
          console.log('🔐 Instalações autorizadas encontradas:', instalacaoIdsPermitidos.length)
          
          // 3. Buscar ordens das instalações autorizadas (que não sejam da própria empresa)
          if (instalacaoIdsPermitidos.length > 0) {
            const { data: ordensPermitidas, error: errorPermitidas } = await supabase
              .from('ordens_servicos')
              .select(`
                id,
                id_cliente,
                id_tipo_servico,
                id_equipe,
                id_empresa,
                status,
                obs_operacional,
                obs_campo,
                data_execucao,
                data_criacao,
                created_at,
                updated_at,
                presenca
              `)
              .in('id_tipo_servico', idTiposGestao)
              .in('id_cliente', instalacaoIdsPermitidos)
              .neq('id_empresa', userEmpresa.id) // Excluir ordens da própria empresa (já estão em ordensEmpresaList)
              .order('created_at', { ascending: false })
            
            if (errorPermitidas) {
              console.error('❌ Erro ao buscar ordens permitidas:', errorPermitidas)
              // Não lançar erro, apenas continuar sem ordens adicionais
            } else {
              const ordensPermitidasList = ordensPermitidas || []
              console.log('📋 Ordens permitidas encontradas:', ordensPermitidasList.length)
              
              // 4. Combinar ordens (usar Map para evitar duplicatas)
              const ordensMap = new Map()
              ordensEmpresaList.forEach(ordem => {
                ordensMap.set(ordem.id, ordem)
              })
              ordensPermitidasList.forEach(ordem => {
                ordensMap.set(ordem.id, ordem)
              })
              
              // Converter para array e ordenar por data (mais recente primeiro)
              ordensData = Array.from(ordensMap.values()).sort((a, b) => {
                const dateA = new Date(a.created_at || a.data_criacao || 0)
                const dateB = new Date(b.created_at || b.data_criacao || 0)
                return dateB - dateA
              })
            }
          } else {
            ordensData = ordensEmpresaList
          }
          
          console.log('📋 Total de ordens encontradas (com permissões):', ordensData.length)
        }
      }
      
      // Buscar dados relacionados em lote para melhor performance
      if (ordensData && ordensData.length > 0) {
        const clienteIds = [...new Set(ordensData.map(o => o.id_cliente).filter(Boolean))]
        const empresaIds = [...new Set(ordensData.map(o => o.id_empresa).filter(Boolean))]
        const tipoServicoIds = [...new Set(ordensData.map(o => o.id_tipo_servico).filter(Boolean))]
        
        // Buscar todos os clientes (endereço, fachada, link mapa) para o modal de opções
        const { data: clientesData } = await supabase
          .from('clientes_cadastro')
          .select('id, nome_completo, cep, logradouro, numero, complemento, bairro, cidade, estado, foto_fachada, link_localizacao')
          .in('id', clienteIds)
        
        // Buscar kit solar dos clientes
        const { data: kitsData } = await supabase
          .from('kit_solar_cliente')
          .select('id_cliente, marca_inversor, potencia_inversor, quantidade_inversor, marca_modulo, potencia_modulo, quantidade_modulo')
          .in('id_cliente', clienteIds)
        
        // Buscar todas as empresas de uma vez
        const { data: empresasData } = await supabase
          .from('empresa')
          .select('id, empresa')
          .in('id', empresaIds)
        
        // Buscar todos os tipos de serviço das ordens
        const { data: tiposServicoData } = await supabase
          .from('tipo_servico')
          .select('id, nome')
          .in('id', tipoServicoIds)
        
        const clientesMap = new Map((clientesData || []).map(c => [c.id, c]))
        const kitMap = new Map((kitsData || []).map(k => [k.id_cliente, k]))
        const empresasMap = new Map((empresasData || []).map(e => [e.id, e.empresa]))
        const tiposServicoMap = new Map((tiposServicoData || []).map(t => [t.id, t.nome]))

        const { data: clientesBaixaData, error: clientesBaixaError } = await supabase
          .from('clientes')
          .select('id, id_cliente_cadastro, tipo_servico_id, medicao_fechada')
          .in('id_cliente_cadastro', clienteIds)
        if (clientesBaixaError) {
          console.warn('Não foi possível carregar medicao_fechada em clientes:', clientesBaixaError.message)
        }
        const medicaoPorClienteTipo = new Map()
        ;(clientesBaixaData || []).forEach((c) => {
          if (!c.medicao_fechada || !c.id_cliente_cadastro || !c.tipo_servico_id) return
          medicaoPorClienteTipo.set(`${c.id_cliente_cadastro}|${c.tipo_servico_id}`, true)
        })
        
        const ordensCompletas = ordensData.map(ordem => {
          const clienteObj = clientesMap.get(ordem.id_cliente)
          const chaveTipo = ordem.id_cliente && ordem.id_tipo_servico
            ? `${ordem.id_cliente}|${ordem.id_tipo_servico}`
            : ''
          return {
            ...ordem,
            cliente: clienteObj ? { ...clienteObj, nome_completo: clienteObj.nome_completo || 'N/A' } : { nome_completo: 'N/A' },
            kit_solar: kitMap.get(ordem.id_cliente) || null,
            tipo_servico: { nome: tiposServicoMap.get(ordem.id_tipo_servico) || 'N/A' },
            empresa: { empresa: empresasMap.get(ordem.id_empresa) || 'N/A' },
            medicao_fechada: !!(chaveTipo && medicaoPorClienteTipo.get(chaveTipo))
          }
        })
        
        setOrdensServico(ordensCompletas)
      } else {
        setOrdensServico([])
      }
    } catch (error) {
      console.error('Erro ao carregar ordens de instalação:', error)
    } finally {
      setIsLoadingOrdens(false)
    }
  }

  // Filtrar ordens
  const statusSelecionados = Array.isArray(filtros.status) ? filtros.status : (filtros.status ? [filtros.status] : [])
  useEffect(() => {
    const el = statusTodosCheckRef.current
    if (!el) return
    el.indeterminate = statusSelecionados.length > 0 && statusSelecionados.length < STATUS_OPCOES.length
  }, [statusSelecionados.length])
  const ordensFiltradas = ordensServico.filter(ordem => {
    const matchCliente = !filtros.cliente || 
      ordem.cliente?.nome_completo?.toLowerCase().includes(filtros.cliente.toLowerCase())
    
    const matchStatus = statusSelecionados.length === 0 || statusSelecionados.includes(ordem.status)
    
    const matchDataInicio = !filtros.dataInicio || 
      (ordem.data_execucao && ordem.data_execucao >= filtros.dataInicio)
    
    const matchDataFim = !filtros.dataFim || 
      (ordem.data_execucao && ordem.data_execucao <= filtros.dataFim)
    
    return matchCliente && matchStatus && matchDataInicio && matchDataFim
  })

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleStatusToggle = (status) => {
    setFiltros(prev => {
      const current = Array.isArray(prev.status) ? prev.status : (prev.status ? [prev.status] : [])
      const next = current.includes(status)
        ? current.filter(s => s !== status)
        : [...current, status]
      return { ...prev, status: next }
    })
  }

  const handleStatusSelectAll = (checked) => {
    setFiltros(prev => ({
      ...prev,
      status: checked ? [...STATUS_OPCOES] : []
    }))
  }

  // Função para obter classe do badge de status
  const getStatusBadgeClass = (status) => {
    if (!status) return 'bg-secondary'
    const statusLower = status.toLowerCase()
    if (statusLower.includes('novo')) return 'bg-primary'
    if (statusLower.includes('andamento')) return 'bg-warning'
    if (statusLower.includes('concluído') || statusLower.includes('concluido')) return 'bg-success'
    if (statusLower.includes('cancelado')) return 'bg-danger'
    return 'bg-secondary'
  }

  // Carregar equipes
  const loadEquipes = async () => {
    try {
      let query = supabase
        .from('equipes')
        .select('id, nome')
        .eq('ativo', true)
      
      // Filtrar por empresa se o usuário não for administrador
      if (userRole !== 'administrador' && userEmpresa?.id) {
        query = query.eq('empresa_id', userEmpresa.id)
      }
      
      const { data, error } = await query.order('nome')
      
      if (error) throw error
      setEquipes(data || [])
    } catch (error) {
      console.error('Erro ao carregar equipes:', error)
    }
  }

  // Carregar veículos
  const loadVeiculos = async () => {
    try {
      let query = supabase
        .from('veiculos')
        .select('id, veiculo, placa')
        .eq('ativo', true)
      
      // Filtrar por empresa se o usuário não for administrador
      if (userRole !== 'administrador' && userEmpresa?.id) {
        query = query.eq('empresa_id', userEmpresa.id)
      }
      
      const { data, error } = await query.order('veiculo')
      
      if (error) throw error
      setVeiculos(data || [])
    } catch (error) {
      console.error('Erro ao carregar veículos:', error)
    }
  }

  // Carregar colaboradores
  const loadColaboradores = async () => {
    try {
      let query = supabase
        .from('colaboradores')
        .select('id, nome, cargo')
        .eq('ativo', true)
      
      // Filtrar por empresa se o usuário não for administrador
      if (userRole !== 'administrador' && userEmpresa?.id) {
        query = query.eq('empresa_id', userEmpresa.id)
      }
      
      const { data, error } = await query.order('nome')
      
      if (error) throw error
      setColaboradores(data || [])
    } catch (error) {
      console.error('Erro ao carregar colaboradores:', error)
    }
  }

  // Abrir modal de presença ou confirmação de início
  const handleOpenPresencaModal = async (ordem) => {
    if (ordem.status !== 'Novo') return
    
    if (ordem.presenca) {
      // Se precisa preencher presença, abrir modal de presença
      // Recarregar dados para garantir que estão atualizados
      await Promise.all([
        loadEquipes(),
        loadVeiculos(),
        loadColaboradores()
      ])
      
      setOrdemSelecionada(ordem)
      setPresencaFormData({
        data_presenca: new Date().toISOString().split('T')[0],
        data_cadastro_preenchido: new Date().toISOString().split('T')[0],
        equipe_id: '',
        veiculo_id: '',
        km_inicial: '',
        observacoes: '',
        colaboradores: []
      })
      setShowPresencaModal(true)
    } else {
      // Se não precisa preencher presença, mostrar modal de confirmação
      setOrdemParaIniciar(ordem)
      setShowConfirmInicioModal(true)
    }
  }

  // Fechar modal de presença
  const handleClosePresencaModal = () => {
    setShowPresencaModal(false)
    setOrdemSelecionada(null)
    setPresencaFormData({
      data_presenca: '',
      data_cadastro_preenchido: '',
      equipe_id: '',
      veiculo_id: '',
      km_inicial: '',
      observacoes: '',
      colaboradores: []
    })
  }

  // Fechar modal de confirmação de início
  const handleCloseConfirmInicioModal = () => {
    setShowConfirmInicioModal(false)
    setOrdemParaIniciar(null)
  }

  // Confirmar início do serviço
  const handleConfirmarInicioServico = async () => {
    if (!ordemParaIniciar) return

    try {
      setIsLoadingInicio(true)

      // Atualizar status da ordem de serviço para "Em andamento"
      const { error: updateError } = await supabase
        .from('ordens_servicos')
        .update({ status: 'Em andamento' })
        .eq('id', ordemParaIniciar.id)
      
      if (updateError) throw updateError

      setSuccessMessage('Serviço iniciado com sucesso! Status atualizado para "Em andamento".')
      setShowSuccessModal(true)
      handleCloseConfirmInicioModal()
      // Recarregar ordens para atualizar a lista
      await loadOrdensServico()
    } catch (error) {
      console.error('Erro ao iniciar serviço:', error)
      alert('Erro ao iniciar serviço: ' + error.message)
    } finally {
      setIsLoadingInicio(false)
    }
  }

  // Carregar dados de baixa para visualização
  // tipoFormulario distingue a baixa por tipo de serviço (ex.: Instalação x Obra Cívil),
  // já que um mesmo cliente pode ter várias ordens/baixas em formularios_baixa_resolve.
  const loadDadosBaixa = async (clienteId, tipoFormulario = null, tipoServicoId = null) => {
    try {
      setIsLoadingVisualizarBaixa(true)
      
      // Carregar dados da baixa
      let baixaQuery = supabase
        .from('formularios_baixa_resolve')
        .select('*')
        .eq('cliente_id', clienteId)
      if (tipoFormulario) {
        baixaQuery = baixaQuery.eq('tipo_formulario', tipoFormulario)
      }
      const { data: baixaData, error: baixaError } = await baixaQuery
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
      
      if (baixaError && baixaError.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw baixaError
      }

      // Carregar dados do cliente (tipo_padrao_id, quantidade_modulos, configuracao_inversor, nota_material, valor_material)
      // Buscar de clientes (não clientes_cadastro) pois esses campos estão em clientes
      let dadosCliente = null
      if (clienteId) {
        let clienteQuery = supabase
          .from('clientes')
          .select('tipo_padrao_id, quantidade_modulos, configuracao_inversor, nota_material, valor_material, valor_obra')
          .eq('id_cliente_cadastro', clienteId)
        if (tipoServicoId) clienteQuery = clienteQuery.eq('tipo_servico_id', tipoServicoId)
        const { data: clienteData, error: clienteError } = await clienteQuery
          .order('data_instalacao', { ascending: false })
          .limit(1)
          .maybeSingle()
        
        if (!clienteError && clienteData) {
          dadosCliente = clienteData
        } else {
          // Se não encontrar em clientes, tentar clientes_cadastro (para valor_material)
          const { data: clienteCadastroData, error: clienteCadastroError } = await supabase
            .from('clientes_cadastro')
            .select('valor_material')
            .eq('id', clienteId)
            .single()
          
          if (!clienteCadastroError && clienteCadastroData) {
            dadosCliente = {
              tipo_padrao_id: null,
              quantidade_modulos: null,
              configuracao_inversor: false,
              nota_material: false,
              valor_material: clienteCadastroData.valor_material || null,
              valor_obra: null
            }
          }
        }
      }

      // Buscar nome do tipo de padrão se houver tipo_padrao_id
      let nomeTipoPadrao = null
      if (dadosCliente?.tipo_padrao_id) {
        const { data: tipoPadraoData, error: tipoPadraoError } = await supabase
          .from('tipo_padrao')
          .select('nome')
          .eq('id', dadosCliente.tipo_padrao_id)
          .single()
        
        if (!tipoPadraoError && tipoPadraoData) {
          nomeTipoPadrao = tipoPadraoData.nome
        }
      }

      // Combinar dados da baixa com dados do cliente
      setDadosBaixa({
        ...(baixaData || {}),
        tipo_padrao_id: dadosCliente?.tipo_padrao_id || null,
        tipo_padrao_nome: nomeTipoPadrao,
        quantidade_modulos: dadosCliente?.quantidade_modulos || null,
        configuracao_inversor: dadosCliente?.configuracao_inversor || false,
        nota_material: dadosCliente?.nota_material || false,
        valor_material: dadosCliente?.valor_material || null,
        valor_obra: dadosCliente?.valor_obra || null
      })
    } catch (error) {
      console.error('Erro ao carregar dados de baixa:', error)
      setDadosBaixa(null)
    } finally {
      setIsLoadingVisualizarBaixa(false)
    }
  }

  const verificarMedicaoFechadaOrdem = async (ordem) => {
    if (ordem?.medicao_fechada) return true
    if (!ordem?.id_cliente) return false
    const { data, error } = await supabase
      .from('clientes')
      .select('medicao_fechada, tipo_servico_id')
      .eq('id_cliente_cadastro', ordem.id_cliente)
    if (error || !data?.length) return false
    const doMesmoTipo = ordem.id_tipo_servico
      ? data.filter((c) => String(c.tipo_servico_id) === String(ordem.id_tipo_servico))
      : data
    return (doMesmoTipo.length > 0 ? doMesmoTipo : []).some((c) => c.medicao_fechada)
  }

  // Abrir modal de visualização de baixa (Finalizado ou Validado)
  const handleOpenVisualizarBaixa = async (ordem) => {
    if (ordem.status === 'Finalizado' || ordem.status === 'Validado') {
      setOrdemParaVisualizar(ordem)
      setShowVisualizarBaixaModal(true)
      await loadDadosBaixa(ordem.id_cliente, ordem.tipo_servico?.nome || null, ordem.id_tipo_servico)
    }
  }

  // Fechar modal de visualização de baixa
  const handleCloseVisualizarBaixa = () => {
    setShowVisualizarBaixaModal(false)
    setOrdemParaVisualizar(null)
    setDadosBaixa(null)
  }

  // Abrir modal de confirmação de exclusão
  const handleOpenConfirmExcluirBaixa = () => {
    setShowConfirmExcluirBaixaModal(true)
  }

  // Fechar modal de confirmação de exclusão
  const handleCloseConfirmExcluirBaixa = () => {
    setShowConfirmExcluirBaixaModal(false)
  }

  // Excluir baixa e fotos
  const handleExcluirBaixa = async () => {
    if (!dadosBaixa || !ordemParaVisualizar) return

    if (await verificarMedicaoFechadaOrdem(ordemParaVisualizar)) {
      alert('Medição fechada em Serviços. Não é possível excluir esta baixa.')
      return
    }

    // Fechar modal de confirmação
    handleCloseConfirmExcluirBaixa()

    try {
      setIsExcluindoBaixa(true)

      // 1. Extrair e excluir todas as fotos do bucket
      console.log('🗑️ Iniciando exclusão de fotos do bucket...')
      if (dadosBaixa.fotos && typeof dadosBaixa.fotos === 'object') {
        const fotosParaExcluir = []
        
        // Coletar todas as URLs de fotos
        Object.entries(dadosBaixa.fotos).forEach(([campo, urls]) => {
          if (Array.isArray(urls)) {
            urls.forEach(url => {
              if (url && typeof url === 'string') {
                fotosParaExcluir.push(url)
              }
            })
          } else if (urls && typeof urls === 'string') {
            fotosParaExcluir.push(urls)
          }
        })

        console.log(`📸 Total de fotos encontradas: ${fotosParaExcluir.length}`)

        // Extrair caminhos dos arquivos das URLs
        const caminhosParaExcluir = fotosParaExcluir
          .map(url => {
            // Extrair o caminho do bucket da URL
            // URL completa: https://...supabase.co/storage/v1/object/public/fotos-finalizacao/fotos-baixa-resolve/arquivo.jpg
            // Caminho esperado: fotos-baixa-resolve/arquivo.jpg
            
            let caminho = url
            
            // Se for uma URL completa do Supabase Storage
            if (url.includes('/storage/v1/object/public/fotos-finalizacao/')) {
              const match = url.match(/\/fotos-finalizacao\/(.+)$/)
              caminho = match ? match[1] : null
            } else if (url.includes('fotos-finalizacao/')) {
              // Se já contém o prefixo do bucket, extrair apenas a parte após
              const match = url.match(/fotos-finalizacao\/(.+)$/)
              caminho = match ? match[1] : null
            } else if (url.startsWith('fotos-baixa-resolve/')) {
              // Se já é o caminho direto, usar como está
              caminho = url
            }
            
            return caminho
          })
          .filter(Boolean)

        console.log(`📁 Caminhos para excluir: ${caminhosParaExcluir.length}`)
        console.log('📋 Caminhos:', caminhosParaExcluir)

        // Excluir fotos do bucket
        if (caminhosParaExcluir.length > 0) {
          const { data: deletedFiles, error: deleteError } = await supabase.storage
            .from('fotos-finalizacao')
            .remove(caminhosParaExcluir)

          if (deleteError) {
            console.error('❌ Erro ao excluir fotos do bucket:', deleteError)
            // Continuar mesmo se houver erro ao excluir fotos
          } else {
            console.log('✅ Fotos excluídas do bucket com sucesso:', deletedFiles)
          }
        } else {
          console.warn('⚠️ Nenhum caminho válido encontrado para exclusão')
        }
      } else {
        console.warn('⚠️ Nenhuma foto encontrada nos dados da baixa')
      }

      // 2. Excluir registro da tabela formularios_baixa_resolve
      console.log(`🗑️ Excluindo registro da tabela formularios_baixa_resolve (ID: ${dadosBaixa.id})...`)
      const { error: deleteBaixaError } = await supabase
        .from('formularios_baixa_resolve')
        .delete()
        .eq('id', dadosBaixa.id)

      if (deleteBaixaError) {
        console.error('❌ Erro ao excluir registro de formularios_baixa_resolve:', deleteBaixaError)
        throw deleteBaixaError
      } else {
        console.log('✅ Registro excluído de formularios_baixa_resolve com sucesso')
      }

      // 3. Buscar ID do status "Em andamento" na tabela status_clientes
      const { data: statusEmAndamentoData, error: statusEmAndamentoError } = await supabase
        .from('status_clientes')
        .select('id')
        .eq('status', 'Em andamento')
        .single()

      // 4. Atualizar status da ordem de serviço para "Em andamento"
      const { error: updateError } = await supabase
        .from('ordens_servicos')
        .update({ status: 'Em andamento' })
        .eq('id', ordemParaVisualizar.id)

      if (updateError) {
        console.error('Erro ao atualizar status da ordem:', updateError)
        // Não lançar erro aqui, pois a baixa já foi excluída
      }

      // 5. Atualizar tabela clientes_cadastro (apenas limpar valor_material)
      // NOTA: clientes_cadastro não tem id_status, configuracao_inversor e nota_material
      if (statusEmAndamentoData) {
        const { error: updateClienteCadastroError } = await supabase
          .from('clientes_cadastro')
          .update({ valor_material: null })
          .eq('id', ordemParaVisualizar.id_cliente)

        if (updateClienteCadastroError) {
          console.error('Erro ao atualizar clientes_cadastro:', updateClienteCadastroError)
        }
      }

      // 6. Excluir só a linha de `clientes` deste tipo de serviço (não apaga Instalação ao excluir Obra Civil)
      let deleteClientesQuery = supabase
        .from('clientes')
        .delete()
        .eq('id_cliente_cadastro', ordemParaVisualizar.id_cliente)
      if (ordemParaVisualizar.id_tipo_servico) {
        deleteClientesQuery = deleteClientesQuery.eq('tipo_servico_id', ordemParaVisualizar.id_tipo_servico)
      }
      const { error: deleteClientesError } = await deleteClientesQuery

      if (deleteClientesError) {
        console.error('Erro ao excluir registro da tabela clientes:', deleteClientesError)
      } else {
        console.log('✅ Registro excluído da tabela clientes com sucesso')
      }

      // Sucesso
      setSuccessMessage('Baixa excluída com sucesso! Status atualizado para "Em andamento".')
      setShowSuccessModal(true)
      handleCloseVisualizarBaixa()
      
      // Recarregar ordens para atualizar a lista
      await loadOrdensServico()
    } catch (error) {
      console.error('Erro ao excluir baixa:', error)
      alert('Erro ao excluir baixa: ' + error.message)
    } finally {
      setIsExcluindoBaixa(false)
    }
  }

  const getEmptyBaixaFormData = () => ({
    tipo_formulario: 'Instalação',
    tipo_padrao_id: '',
    tipo_fase: '',
    tipo_inversor: '',
    quantidade_modulos: '',
    foto_fachada: [],
    foto_padrao_completo: [],
    foto_fase1_neutro: [],
    foto_terra_neutro: [],
    foto_fase1_fase2: [],
    foto_fase2_neutro: [],
    foto_fase2_fase3: [],
    foto_fase3_fase1: [],
    foto_fase3_neutro: [],
    foto_disjuntor_padrao: [],
    foto_aterramento_padrao: [],
    foto_conexoes_padrao: [],
    foto_instalacao_inversor: [],
    foto_qpca: [],
    foto_disjuntor_qpca: [],
    foto_aterramento_qpca: [],
    foto_medicoes_strings: [],
    foto_planta_monitoramento: [],
    foto_etiqueta_inversor: [],
    foto_datalogger: [],
    foto_fase1_terra_qpca: [],
    foto_fase2_terra_qpca: [],
    foto_fase3_terra_qpca: [],
    foto_fase1_fase2_qpca: [],
    foto_fase2_fase3_qpca: [],
    foto_fase3_fase1_qpca: [],
    foto_antes_modulos: [],
    foto_apos_modulos: [],
    foto_strings: [],
    foto_aterramento_modulos: [],
    foto_aterramento_trilhos: [],
    foto_antes_manutencao_correcao: [],
    foto_apos_manutencao_correcao: [],
    observacoes: '',
    configuracao_inversor: false,
    nota_material: false,
    valor_material: '',
    valor_obra: ''
  })

  const loadBaixaParaEdicao = async (clienteId, tipoFormulario = null, tipoServicoId = null) => {
    if (!clienteId) return
    setIsLoadingBaixaParaEdicao(true)
    setBaixaExistenteId(null)
    try {
      let baixaQuery = supabase
        .from('formularios_baixa_resolve')
        .select('*')
        .eq('cliente_id', clienteId)
      if (tipoFormulario) {
        baixaQuery = baixaQuery.eq('tipo_formulario', tipoFormulario)
      }
      const { data: baixaData, error: baixaError } = await baixaQuery
        .order('created_at', { ascending: false })
        .limit(1)
        .single()
      if (baixaError && baixaError.code !== 'PGRST116') throw baixaError
      if (!baixaData) {
        setBaixaFormData(getEmptyBaixaFormData())
        setIsLoadingBaixaParaEdicao(false)
        return
      }
      setBaixaExistenteId(baixaData.id)
      const fotos = baixaData.fotos || {}
      const arr = (v) => (Array.isArray(v) ? v : v ? [v] : [])
      let dadosCliente = null
      let clienteQuery = supabase
        .from('clientes')
        .select('tipo_padrao_id, quantidade_modulos, configuracao_inversor, nota_material, valor_material, valor_obra')
        .eq('id_cliente_cadastro', clienteId)
      if (tipoServicoId) clienteQuery = clienteQuery.eq('tipo_servico_id', tipoServicoId)
      const { data: clienteData } = await clienteQuery
        .order('data_instalacao', { ascending: false })
        .limit(1)
        .maybeSingle()
      if (clienteData) dadosCliente = clienteData
      else {
        const { data: cc } = await supabase.from('clientes_cadastro').select('valor_material').eq('id', clienteId).single()
        if (cc) dadosCliente = { tipo_padrao_id: null, quantidade_modulos: null, configuracao_inversor: false, nota_material: false, valor_material: cc.valor_material, valor_obra: null }
      }
      setBaixaFormData({
        ...getEmptyBaixaFormData(),
        tipo_formulario: baixaData.tipo_formulario || 'Instalação',
        tipo_padrao_id: dadosCliente?.tipo_padrao_id || '',
        tipo_fase: baixaData.tipo_fase || '',
        tipo_inversor: baixaData.tipo_inversor || '',
        quantidade_modulos: String(dadosCliente?.quantidade_modulos ?? ''),
        observacoes: baixaData.observacao || '',
        configuracao_inversor: dadosCliente?.configuracao_inversor ?? false,
        nota_material: dadosCliente?.nota_material ?? false,
        valor_material: dadosCliente?.valor_material != null ? String(dadosCliente.valor_material) : '',
        valor_obra: formatarValorParaMascara(dadosCliente?.valor_obra),
        foto_fachada: arr(fotos.foto_fachada),
        foto_padrao_completo: arr(fotos.foto_padrao_completo),
        foto_fase1_neutro: arr(fotos.foto_fase1_neutro),
        foto_terra_neutro: arr(fotos.foto_terra_neutro),
        foto_fase1_fase2: arr(fotos.foto_fase1_fase2),
        foto_fase2_neutro: arr(fotos.foto_fase2_neutro),
        foto_fase2_fase3: arr(fotos.foto_fase2_fase3),
        foto_fase3_fase1: arr(fotos.foto_fase3_fase1),
        foto_fase3_neutro: arr(fotos.foto_fase3_neutro),
        foto_disjuntor_padrao: arr(fotos.foto_disjuntor_padrao),
        foto_aterramento_padrao: arr(fotos.foto_aterramento_padrao),
        foto_conexoes_padrao: arr(fotos.foto_conexoes_padrao),
        foto_instalacao_inversor: arr(fotos.foto_instalacao_inversor),
        foto_qpca: arr(fotos.foto_qpca),
        foto_disjuntor_qpca: arr(fotos.foto_disjuntor_qpca),
        foto_aterramento_qpca: arr(fotos.foto_aterramento_qpca),
        foto_medicoes_strings: arr(fotos.foto_medicoes_strings),
        foto_planta_monitoramento: arr(fotos.foto_planta_monitoramento),
        foto_etiqueta_inversor: arr(fotos.foto_etiqueta_inversor),
        foto_datalogger: arr(fotos.foto_datalogger),
        foto_fase1_terra_qpca: arr(fotos.foto_fase1_terra_qpca),
        foto_fase2_terra_qpca: arr(fotos.foto_fase2_terra_qpca),
        foto_fase3_terra_qpca: arr(fotos.foto_fase3_terra_qpca),
        foto_fase1_fase2_qpca: arr(fotos.foto_fase1_fase2_qpca),
        foto_fase2_fase3_qpca: arr(fotos.foto_fase2_fase3_qpca),
        foto_fase3_fase1_qpca: arr(fotos.foto_fase3_fase1_qpca),
        foto_antes_modulos: arr(fotos.foto_antes_modulos),
        foto_apos_modulos: arr(fotos.foto_apos_modulos),
        foto_strings: arr(fotos.foto_strings),
        foto_aterramento_modulos: arr(fotos.foto_aterramento_modulos),
        foto_aterramento_trilhos: arr(fotos.foto_aterramento_trilhos),
        foto_antes_manutencao_correcao: arr(fotos.foto_antes_manutencao_correcao),
        foto_apos_manutencao_correcao: arr(fotos.foto_apos_manutencao_correcao)
      })
      const preview = {}
      Object.entries(fotos).forEach(([k, v]) => {
        const a = Array.isArray(v) ? v : v ? [v] : []
        if (a.length) {
          // Preview espera objetos com .url para o img src; URLs carregadas são strings
          preview[k] = a.map(u => (typeof u === 'string' ? { url: u } : u))
        }
      })
      setFotosPreview(preview)
    } catch (e) {
      console.error('Erro ao carregar baixa para edição:', e)
      setBaixaFormData(getEmptyBaixaFormData())
      setBaixaExistenteId(null)
    } finally {
      setIsLoadingBaixaParaEdicao(false)
    }
  }

  // Abrir modal de baixa
  const handleOpenBaixaModal = async (ordem) => {
    if (await verificarMedicaoFechadaOrdem(ordem)) {
      alert('Medição fechada em Serviços. Não é possível editar esta baixa.')
      return
    }
    if (ordem.status === 'Em andamento') {
      setBaixaExistenteId(null)
      setOrdemParaBaixa(ordem)
      setBaixaFormData(getEmptyBaixaFormData())
      setFotosPreview({})
      setShowBaixaModal(true)
    } else if (ordem.status === 'Finalizado') {
      setOrdemParaBaixa(ordem)
      setBaixaFormData(getEmptyBaixaFormData())
      setFotosPreview({})
      setShowBaixaModal(true)
      loadBaixaParaEdicao(ordem.id_cliente, ordem.tipo_servico?.nome || null, ordem.id_tipo_servico)
    }
  }

  // Fechar modal de baixa
  const handleCloseBaixaModal = () => {
    setShowBaixaModal(false)
    setOrdemParaBaixa(null)
    setBaixaExistenteId(null)
    setBaixaFormData(getEmptyBaixaFormData())
    setFotosPreview({})
  }

  // Upload de fotos genérico
  const handleFotosUpload = async (e, campo) => {
    const files = Array.from(e.target.files)
    if (files.length === 0) return

    try {
      const uploadedUrls = []
      const previews = []

      for (const file of files) {
        // Validar tipo de arquivo
        if (!file.type.startsWith('image/')) {
          alert(`O arquivo ${file.name} não é uma imagem válida.`)
          continue
        }

        // Validar tamanho (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          alert(`A imagem ${file.name} é muito grande. Tamanho máximo: 10MB.`)
          continue
        }

        // Gerar nome único para o arquivo com data e hora para foto_fachada
        const timestamp = Date.now()
        const randomStr = Math.random().toString(36).substring(2, 15)
        const fileExt = file.name.split('.').pop()
        let fileName
        if (campo === 'foto_fachada') {
          const agora = new Date()
          const dataHora = `${agora.getFullYear()}${String(agora.getMonth() + 1).padStart(2, '0')}${String(agora.getDate()).padStart(2, '0')}_${String(agora.getHours()).padStart(2, '0')}${String(agora.getMinutes()).padStart(2, '0')}${String(agora.getSeconds()).padStart(2, '0')}`
          fileName = `${ordemParaBaixa.id}_${campo}_${dataHora}_${randomStr}.${fileExt}`
        } else {
          fileName = `${ordemParaBaixa.id}_${campo}_${timestamp}_${randomStr}.${fileExt}`
        }
        const filePath = `fotos-baixa-resolve/${fileName}`

        // Upload para Supabase Storage
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('fotos-finalizacao')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          })

        if (uploadError) {
          console.error('Erro ao fazer upload:', uploadError)
          alert(`Erro ao fazer upload de ${file.name}: ${uploadError.message}`)
          continue
        }

        // Obter URL pública
        const { data: { publicUrl } } = supabase.storage
          .from('fotos-finalizacao')
          .getPublicUrl(filePath)

        uploadedUrls.push(publicUrl)
        previews.push({
          url: publicUrl,
          name: file.name,
          path: filePath
        })
      }

      // Atualizar estado
      setBaixaFormData(prev => ({
        ...prev,
        [campo]: [...(prev[campo] || []), ...uploadedUrls]
      }))
      setFotosPreview(prev => ({
        ...prev,
        [campo]: [...(prev[campo] || []), ...previews]
      }))

      // Limpar input
      e.target.value = ''
    } catch (error) {
      console.error('Erro ao fazer upload das fotos:', error)
      alert('Erro ao fazer upload das fotos: ' + error.message)
    }
  }

  // Remover foto
  const handleRemoveFoto = (campo, index) => {
    setBaixaFormData(prev => ({
      ...prev,
      [campo]: prev[campo].filter((_, i) => i !== index)
    }))
    setFotosPreview(prev => ({
      ...prev,
      [campo]: (prev[campo] || []).filter((_, i) => i !== index)
    }))
  }

  // Função auxiliar para renderizar campo de upload de foto
  const renderFotoField = (campo, label, obrigatorio = true, multiplo = false, descricao = '') => {
    const fotos = baixaFormData[campo] || []
    const previews = fotosPreview[campo] || []
    
    return (
      <div className="col-12">
        <label htmlFor={campo} className="form-label">
          {label} {obrigatorio && '*'}
        </label>
        <input
          type="file"
          id={campo}
          className="form-control"
          accept="image/*"
          multiple={multiplo}
          onChange={(e) => handleFotosUpload(e, campo)}
          required={obrigatorio && fotos.length === 0}
        />
        {descricao && <small className="text-muted d-block">{descricao}</small>}
        
        {/* Preview das fotos */}
        {previews.length > 0 && (
          <div className="mt-3">
            <div className="row g-2">
              {previews.map((foto, index) => (
                <div key={index} className="col-md-3 col-6">
                  <div className="position-relative">
                    <img
                      src={typeof foto === 'string' ? foto : (foto?.url || foto)}
                      alt={`Preview ${index + 1}`}
                      className="img-thumbnail"
                      style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                    />
                    <button
                      type="button"
                      className="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                      onClick={() => handleRemoveFoto(campo, index)}
                      style={{ zIndex: 10 }}
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <small className="text-muted d-block mt-2">
              {previews.length} foto(s) adicionada(s)
            </small>
          </div>
        )}
      </div>
    )
  }

  // Validar campos obrigatórios
  const validarCamposObrigatorios = () => {
    const isManutencaoCorretiva = ordemParaBaixa?.tipo_servico?.nome !== 'Instalação'

    if (isManutencaoCorretiva) {
      if (baixaFormData.foto_antes_manutencao_correcao?.length === 0) {
        alert('Por favor, adicione pelo menos uma foto antes do serviço.')
        return false
      }
      if (baixaFormData.foto_apos_manutencao_correcao?.length === 0) {
        alert('Por favor, adicione pelo menos uma foto após o serviço.')
        return false
      }
      return true
    }

    // Campos sempre obrigatórios para Instalação
    if (!baixaFormData.tipo_fase) {
      alert('Por favor, selecione o tipo de fase.')
      return false
    }

    if (baixaFormData.foto_fachada.length === 0) {
      alert('Por favor, adicione a foto da fachada.')
      return false
    }

    if (baixaFormData.foto_padrao_completo.length === 0) {
      alert('Por favor, adicione a foto do padrão completo.')
      return false
    }

    // Campos condicionais por tipo_fase
    if (baixaFormData.tipo_fase === 'Monofásico') {
      if (baixaFormData.foto_fase1_neutro.length === 0) {
        alert('Por favor, adicione a foto Fase 1 e Neutro.')
        return false
      }
      if (baixaFormData.foto_terra_neutro.length === 0) {
        alert('Por favor, adicione a foto Terra e Neutro.')
        return false
      }
    } else if (baixaFormData.tipo_fase === 'Bifásico') {
      if (baixaFormData.foto_fase1_fase2.length === 0) {
        alert('Por favor, adicione a foto Fase 1 e Fase 2.')
        return false
      }
      if (baixaFormData.foto_fase1_neutro.length === 0) {
        alert('Por favor, adicione a foto Fase 1 e Neutro.')
        return false
      }
      if (baixaFormData.foto_fase2_neutro.length === 0) {
        alert('Por favor, adicione a foto Fase 2 e Neutro.')
        return false
      }
      if (baixaFormData.foto_terra_neutro.length === 0) {
        alert('Por favor, adicione a foto Terra e Neutro.')
        return false
      }
    } else if (baixaFormData.tipo_fase === 'Trifásico') {
      if (baixaFormData.foto_fase1_fase2.length === 0) {
        alert('Por favor, adicione a foto Fase 1 e Fase 2.')
        return false
      }
      if (baixaFormData.foto_fase2_fase3.length === 0) {
        alert('Por favor, adicione a foto Fase 2 e Fase 3.')
        return false
      }
      if (baixaFormData.foto_fase3_fase1.length === 0) {
        alert('Por favor, adicione a foto Fase 3 e Fase 1.')
        return false
      }
      if (baixaFormData.foto_fase1_neutro.length === 0) {
        alert('Por favor, adicione a foto Fase 1 e Neutro.')
        return false
      }
      if (baixaFormData.foto_fase2_neutro.length === 0) {
        alert('Por favor, adicione a foto Fase 2 e Neutro.')
        return false
      }
      if (baixaFormData.foto_fase3_neutro.length === 0) {
        alert('Por favor, adicione a foto Fase 3 e Neutro.')
        return false
      }
      if (baixaFormData.foto_terra_neutro.length === 0) {
        alert('Por favor, adicione a foto Terra e Neutro.')
        return false
      }
    }

    // Campos sempre obrigatórios do padrão
    if (baixaFormData.foto_disjuntor_padrao.length === 0) {
      alert('Por favor, adicione a foto do disjuntor do padrão.')
      return false
    }
    if (baixaFormData.foto_aterramento_padrao.length === 0) {
      alert('Por favor, adicione a foto do aterramento do padrão.')
      return false
    }
    if (baixaFormData.foto_conexoes_padrao.length === 0) {
      alert('Por favor, adicione pelo menos uma foto das conexões do padrão.')
      return false
    }

    // Campos sempre obrigatórios do inversor
    if (baixaFormData.foto_instalacao_inversor.length === 0) {
      alert('Por favor, adicione a foto da instalação do inversor.')
      return false
    }
    if (baixaFormData.foto_qpca.length === 0) {
      alert('Por favor, adicione a foto do QPCA.')
      return false
    }
    if (baixaFormData.foto_disjuntor_qpca.length === 0) {
      alert('Por favor, adicione a foto do disjuntor do QPCA.')
      return false
    }
    if (baixaFormData.foto_aterramento_qpca.length === 0) {
      alert('Por favor, adicione a foto do aterramento do QPCA.')
      return false
    }
    if (baixaFormData.foto_medicoes_strings.length === 0) {
      alert('Por favor, adicione pelo menos uma foto das medições das strings.')
      return false
    }
    if (baixaFormData.configuracao_inversor && baixaFormData.foto_planta_monitoramento.length === 0) {
      alert('Por favor, adicione a foto da planta de monitoramento.')
      return false
    }
    if (baixaFormData.foto_etiqueta_inversor.length === 0) {
      alert('Por favor, adicione a foto da etiqueta do inversor.')
      return false
    }
    if (baixaFormData.foto_datalogger.length === 0) {
      alert('Por favor, adicione a foto do datalogger.')
      return false
    }

    // Validar tipo de inversor e fotos do QPCA
    if (!baixaFormData.tipo_inversor) {
      alert('Por favor, selecione o tipo de inversor.')
      return false
    }

    if (baixaFormData.tipo_inversor === 'Bifásico') {
      if (baixaFormData.foto_fase1_terra_qpca.length === 0) {
        alert('Por favor, adicione a foto Fase 1 e Terra do QPCA.')
        return false
      }
      if (baixaFormData.foto_fase2_terra_qpca.length === 0) {
        alert('Por favor, adicione a foto Fase 2 e Terra do QPCA.')
        return false
      }
      if (baixaFormData.foto_fase1_fase2_qpca.length === 0) {
        alert('Por favor, adicione a foto Fase 1 e Fase 2 do QPCA.')
        return false
      }
    } else if (baixaFormData.tipo_inversor === 'Trifásico') {
      if (baixaFormData.foto_fase1_terra_qpca.length === 0) {
        alert('Por favor, adicione a foto Fase 1 e Terra do QPCA.')
        return false
      }
      if (baixaFormData.foto_fase2_terra_qpca.length === 0) {
        alert('Por favor, adicione a foto Fase 2 e Terra do QPCA.')
        return false
      }
      if (baixaFormData.foto_fase3_terra_qpca.length === 0) {
        alert('Por favor, adicione a foto Fase 3 e Terra do QPCA.')
        return false
      }
      if (baixaFormData.foto_fase1_fase2_qpca.length === 0) {
        alert('Por favor, adicione a foto Fase 1 e Fase 2 do QPCA.')
        return false
      }
      if (baixaFormData.foto_fase2_fase3_qpca.length === 0) {
        alert('Por favor, adicione a foto Fase 2 e Fase 3 do QPCA.')
        return false
      }
      if (baixaFormData.foto_fase3_fase1_qpca.length === 0) {
        alert('Por favor, adicione a foto Fase 3 e Fase 1 do QPCA.')
        return false
      }
    }

    // Campos obrigatórios dos módulos
    if (baixaFormData.foto_antes_modulos.length === 0) {
      alert('Por favor, adicione a foto antes de instalar os módulos.')
      return false
    }
    if (baixaFormData.foto_apos_modulos.length === 0) {
      alert('Por favor, adicione a foto após instalar os módulos.')
      return false
    }
    if (baixaFormData.foto_strings.length === 0) {
      alert('Por favor, adicione pelo menos uma foto das strings.')
      return false
    }

    return true
  }

  // Salvar baixa
  const handleSaveBaixa = async (e) => {
    e.preventDefault()
    
    if (!ordemParaBaixa) return

    if (await verificarMedicaoFechadaOrdem(ordemParaBaixa)) {
      alert('Medição fechada em Serviços. Não é possível editar esta baixa.')
      setIsLoadingBaixa(false)
      return
    }

    // Validar campos obrigatórios
    if (!validarCamposObrigatorios()) {
      return
    }

    const isManutencaoCorretiva = ordemParaBaixa?.tipo_servico?.nome !== 'Instalação'

    try {
      setIsLoadingBaixa(true)

      // Preparar objeto de fotos organizado
      let fotosOrganizadas
      let tipoFaseConvertido
      let tipoFormulario

      if (isManutencaoCorretiva) {
        fotosOrganizadas = {
          foto_antes_manutencao_correcao: baixaFormData.foto_antes_manutencao_correcao || [],
          foto_apos_manutencao_correcao: baixaFormData.foto_apos_manutencao_correcao || []
        }
        tipoFaseConvertido = 'monofasico'
        tipoFormulario = ordemParaBaixa.tipo_servico?.nome || 'Manutenção'
      } else {
      fotosOrganizadas = {
        foto_fachada: baixaFormData.foto_fachada,
        foto_padrao_completo: baixaFormData.foto_padrao_completo,
        foto_disjuntor_padrao: baixaFormData.foto_disjuntor_padrao,
        foto_aterramento_padrao: baixaFormData.foto_aterramento_padrao,
        foto_conexoes_padrao: baixaFormData.foto_conexoes_padrao,
        foto_instalacao_inversor: baixaFormData.foto_instalacao_inversor,
        foto_qpca: baixaFormData.foto_qpca,
        foto_disjuntor_qpca: baixaFormData.foto_disjuntor_qpca,
        foto_aterramento_qpca: baixaFormData.foto_aterramento_qpca,
        foto_medicoes_strings: baixaFormData.foto_medicoes_strings,
        foto_planta_monitoramento: baixaFormData.foto_planta_monitoramento,
        foto_etiqueta_inversor: baixaFormData.foto_etiqueta_inversor,
        foto_datalogger: baixaFormData.foto_datalogger,
        foto_antes_modulos: baixaFormData.foto_antes_modulos,
        foto_apos_modulos: baixaFormData.foto_apos_modulos,
        foto_strings: baixaFormData.foto_strings
      }

      // Adicionar fotos condicionais do padrão
      if (baixaFormData.tipo_fase === 'Monofásico') {
        fotosOrganizadas.foto_fase1_neutro = baixaFormData.foto_fase1_neutro
        fotosOrganizadas.foto_terra_neutro = baixaFormData.foto_terra_neutro
      } else if (baixaFormData.tipo_fase === 'Bifásico') {
        fotosOrganizadas.foto_fase1_fase2 = baixaFormData.foto_fase1_fase2
        fotosOrganizadas.foto_fase1_neutro = baixaFormData.foto_fase1_neutro
        fotosOrganizadas.foto_fase2_neutro = baixaFormData.foto_fase2_neutro
        fotosOrganizadas.foto_terra_neutro = baixaFormData.foto_terra_neutro
      } else if (baixaFormData.tipo_fase === 'Trifásico') {
        fotosOrganizadas.foto_fase1_fase2 = baixaFormData.foto_fase1_fase2
        fotosOrganizadas.foto_fase2_fase3 = baixaFormData.foto_fase2_fase3
        fotosOrganizadas.foto_fase3_fase1 = baixaFormData.foto_fase3_fase1
        fotosOrganizadas.foto_fase1_neutro = baixaFormData.foto_fase1_neutro
        fotosOrganizadas.foto_fase2_neutro = baixaFormData.foto_fase2_neutro
        fotosOrganizadas.foto_fase3_neutro = baixaFormData.foto_fase3_neutro
        fotosOrganizadas.foto_terra_neutro = baixaFormData.foto_terra_neutro
      }

      // Adicionar fotos condicionais do QPCA
      if (baixaFormData.tipo_inversor === 'Bifásico') {
        fotosOrganizadas.foto_fase1_terra_qpca = baixaFormData.foto_fase1_terra_qpca
        fotosOrganizadas.foto_fase2_terra_qpca = baixaFormData.foto_fase2_terra_qpca
        fotosOrganizadas.foto_fase1_fase2_qpca = baixaFormData.foto_fase1_fase2_qpca
      } else if (baixaFormData.tipo_inversor === 'Trifásico') {
        fotosOrganizadas.foto_fase1_terra_qpca = baixaFormData.foto_fase1_terra_qpca
        fotosOrganizadas.foto_fase2_terra_qpca = baixaFormData.foto_fase2_terra_qpca
        fotosOrganizadas.foto_fase3_terra_qpca = baixaFormData.foto_fase3_terra_qpca
        fotosOrganizadas.foto_fase1_fase2_qpca = baixaFormData.foto_fase1_fase2_qpca
        fotosOrganizadas.foto_fase2_fase3_qpca = baixaFormData.foto_fase2_fase3_qpca
        fotosOrganizadas.foto_fase3_fase1_qpca = baixaFormData.foto_fase3_fase1_qpca
      }

      // Adicionar fotos opcionais
      if (baixaFormData.foto_aterramento_modulos.length > 0) {
        fotosOrganizadas.foto_aterramento_modulos = baixaFormData.foto_aterramento_modulos
      }
      if (baixaFormData.foto_aterramento_trilhos.length > 0) {
        fotosOrganizadas.foto_aterramento_trilhos = baixaFormData.foto_aterramento_trilhos
      }

      tipoFormulario = baixaFormData.tipo_formulario

      // Função para converter valores com acentos para minúsculas sem acentos (conforme constraint do banco)
      const converterTipoFase = (valor) => {
        if (!valor || valor.trim() === '') {
          console.error('tipo_fase está vazio ou null')
          return null
        }
        
        const mapeamento = {
          'Monofásico': 'monofasico',
          'Bifásico': 'bifasico',
          'Trifásico': 'trifasico',
          // Caso já esteja sem acento mas com maiúscula
          'Monofasico': 'monofasico',
          'Bifasico': 'bifasico',
          'Trifasico': 'trifasico',
          // Caso já esteja em minúsculas
          'monofasico': 'monofasico',
          'bifasico': 'bifasico',
          'trifasico': 'trifasico'
        }
        
        const valorConvertido = mapeamento[valor.trim()] || valor.trim().toLowerCase()
        
        // Validar se o valor convertido é válido
        const valoresValidos = ['monofasico', 'bifasico', 'trifasico']
        if (!valoresValidos.includes(valorConvertido)) {
          console.error('Valor de tipo_fase inválido:', valor, '->', valorConvertido)
          throw new Error(`Tipo de fase inválido: ${valor}. Valores permitidos: monofasico, bifasico, trifasico`)
        }
        
        return valorConvertido
      }

      // Validar e converter tipo_fase (apenas para Instalação)
      try {
        tipoFaseConvertido = converterTipoFase(baixaFormData.tipo_fase)
      } catch (error) {
        alert(error.message)
        return
      }
      
      if (!tipoFaseConvertido) {
        alert('Por favor, selecione um tipo de fase válido.')
        return
      }
      }

      // Validar cliente_id
      if (!ordemParaBaixa.id_cliente) {
        alert('Erro: Cliente não identificado na ordem de serviço.')
        return
      }

      // Preparar dados para salvar
      const dadosParaSalvar = {
        cliente_id: ordemParaBaixa.id_cliente,
        tipo_formulario: tipoFormulario,
        tipo_fase: tipoFaseConvertido,
        fotos: fotosOrganizadas, // Objeto JSON com todas as fotos organizadas
        observacao: baixaFormData.observacoes || null
      }

      const isEdicao = !!baixaExistenteId

      if (isEdicao) {
        const { error: updateError } = await supabase
          .from('formularios_baixa_resolve')
          .update(dadosParaSalvar)
          .eq('id', baixaExistenteId)
        if (updateError) {
          console.error('Erro ao atualizar formulário de baixa:', updateError)
          throw updateError
        }
      } else {
        const { error: insertError } = await supabase
          .from('formularios_baixa_resolve')
          .insert([dadosParaSalvar])
        if (insertError) {
          console.error('Erro ao inserir formulário de baixa:', insertError)
          throw insertError
        }
      }

      // Atualizar status da ordem de serviço para "Finalizado" apenas quando não for edição
      if (!isEdicao) {
        const { error: updateError } = await supabase
          .from('ordens_servicos')
          .update({ status: 'Finalizado' })
          .eq('id', ordemParaBaixa.id)
        if (updateError) {
          console.error('Erro ao atualizar status da ordem:', updateError)
        }
      }

      // Buscar ID do status "Finalizado" na tabela status_clientes
      const { data: statusData, error: statusError } = await supabase
        .from('status_clientes')
        .select('id')
        .eq('status', 'Finalizado')
        .single()

      if (statusError) {
        console.error('Erro ao buscar status Finalizado:', statusError)
        alert('Erro ao buscar status "Finalizado". Por favor, tente novamente.')
        setIsLoadingBaixa(false)
        return
      } else if (statusData) {
        // Atualizar clientes_cadastro apenas com valor_material (se houver)
        // NOTA: clientes_cadastro não tem id_status, configuracao_inversor e nota_material
        if (baixaFormData.nota_material && baixaFormData.valor_material) {
          const valor = parseFloat(baixaFormData.valor_material.replace(/[^\d,.-]/g, '').replace(',', '.'))
          if (!isNaN(valor)) {
            const { error: updateClienteError } = await supabase
              .from('clientes_cadastro')
              .update({ valor_material: valor })
              .eq('id', ordemParaBaixa.id_cliente)

            if (updateClienteError) {
              console.error('Erro ao atualizar valor_material em clientes_cadastro:', updateClienteError)
              // Não bloquear o processo se este update falhar
            }
          }
        }

        // Atualizar/Inserir na tabela clientes usando id_cliente_cadastro (UPSERT)
        const updateClienteDataClientes = {
          id_status: statusData.id,
          tipo_padrao_id: baixaFormData.tipo_padrao_id || null,
          quantidade_modulos: baixaFormData.quantidade_modulos ? parseInt(baixaFormData.quantidade_modulos) : null,
          configuracao_inversor: baixaFormData.configuracao_inversor,
          nota_material: baixaFormData.nota_material,
          equipe_id: ordemParaBaixa.id_equipe || null
        }

        // Adicionar valor_material se o checkbox nota_material estiver marcado
        console.log('🔍 Verificando valor_material:', {
          nota_material: baixaFormData.nota_material,
          valor_material: baixaFormData.valor_material,
          tipo: typeof baixaFormData.valor_material
        })
        
        if (baixaFormData.nota_material) {
          if (baixaFormData.valor_material && baixaFormData.valor_material.toString().trim() !== '') {
            // Remover formatação e converter vírgula para ponto
            const valorLimpo = baixaFormData.valor_material.toString().replace(/[^\d,.-]/g, '').replace(',', '.')
            const valor = parseFloat(valorLimpo)
            console.log('🔢 Conversão do valor:', {
              original: baixaFormData.valor_material,
              limpo: valorLimpo,
              convertido: valor,
              isNaN: isNaN(valor)
            })
            
            if (!isNaN(valor) && valor > 0) {
              updateClienteDataClientes.valor_material = valor
              console.log('✅ valor_material adicionado ao update:', valor)
            } else {
              console.warn('⚠️ valor_material não é um número válido ou é zero:', baixaFormData.valor_material, '->', valor)
            }
          } else {
            console.warn('⚠️ nota_material marcado mas valor_material está vazio ou inválido')
          }
        }

        // Valor da Obra (independente do checkbox de nota de material)
        updateClienteDataClientes.valor_obra = parseValorMascarado(baixaFormData.valor_obra)
        
        console.log('📋 Dados para update em clientes:', updateClienteDataClientes)

        // Um cliente pode ter uma linha em `clientes` por tipo (Instalação e Obra Civil separadas).
        const tipoServicoIdBaixa = ordemParaBaixa.id_tipo_servico || 'c381ce27-351e-456d-8188-9489c369a3a4'
        let checkClienteQuery = supabase
          .from('clientes')
          .select('id')
          .eq('id_cliente_cadastro', ordemParaBaixa.id_cliente)
        if (ordemParaBaixa.id_tipo_servico) {
          checkClienteQuery = checkClienteQuery.eq('tipo_servico_id', ordemParaBaixa.id_tipo_servico)
        }
        const { data: clienteExiste, error: checkError } = await checkClienteQuery.maybeSingle()

        let erroAoSalvarClientes = false
        let mensagemErro = ''

        if (checkError) {
          console.error('Erro ao verificar cliente na tabela clientes:', checkError)
          erroAoSalvarClientes = true
          mensagemErro = 'Erro ao verificar registro na tabela clientes: ' + checkError.message
        } else if (clienteExiste) {
          // Atualizar registro existente
          // data_instalacao só na primeira baixa; edição não deve alterar a data original
          if (!isEdicao) {
            updateClienteDataClientes.data_instalacao = new Date().toISOString().split('T')[0]
          }
          updateClienteDataClientes.tipo_servico_id = tipoServicoIdBaixa
          
          const { error: updateClientesError } = await supabase
            .from('clientes')
            .update(updateClienteDataClientes)
            .eq('id', clienteExiste.id)

          if (updateClientesError) {
            console.error('Erro ao atualizar clientes:', updateClientesError)
            console.error('Dados tentados:', updateClienteDataClientes)
            erroAoSalvarClientes = true
            mensagemErro = 'Erro ao atualizar tabela clientes: ' + updateClientesError.message
          } else {
            console.log('✅ Tabela clientes atualizada com sucesso')
          }
        } else {
          // Registro não existe - buscar dados do clientes_cadastro e inserir
          console.log('⚠️ Registro não encontrado na tabela clientes. Buscando dados de clientes_cadastro para inserir...')
          
          const { data: clienteCadastro, error: clienteCadastroError } = await supabase
            .from('clientes_cadastro')
            .select('*')
            .eq('id', ordemParaBaixa.id_cliente)
            .single()

          if (clienteCadastroError) {
            console.error('Erro ao buscar dados de clientes_cadastro:', clienteCadastroError)
            erroAoSalvarClientes = true
            mensagemErro = 'Erro ao buscar dados do cliente: ' + clienteCadastroError.message
          } else if (clienteCadastro) {
            // Preparar dados para inserção na tabela clientes
            // NOTA: A tabela clientes tem estrutura diferente de clientes_cadastro
            // Incluir id_cliente_cadastro, nome_cliente (obrigatório), empresa_id (obrigatório) e campos de baixa
            if (!userEmpresa?.id) {
              erroAoSalvarClientes = true
              mensagemErro = 'Erro: Empresa do usuário não identificada. Não é possível salvar na tabela clientes.'
            } else {
              // Concatenar endereço completo de clientes_cadastro
              const partesEndereco = []
              if (clienteCadastro.logradouro) partesEndereco.push(clienteCadastro.logradouro)
              if (clienteCadastro.numero) partesEndereco.push(clienteCadastro.numero)
              if (clienteCadastro.complemento) partesEndereco.push(clienteCadastro.complemento)
              if (clienteCadastro.bairro) partesEndereco.push(clienteCadastro.bairro)
              if (clienteCadastro.cidade) partesEndereco.push(clienteCadastro.cidade)
              if (clienteCadastro.estado) partesEndereco.push(clienteCadastro.estado)
              const enderecoCompleto = partesEndereco.length > 0 ? partesEndereco.join(', ') : null

              const insertClienteData = {
                id_cliente_cadastro: ordemParaBaixa.id_cliente,
                nome_cliente: clienteCadastro.nome_completo || 'Cliente sem nome',
                // Usar a empresa do cliente cadastrado (clientes_cadastro), não a do usuário logado
                empresa_id: clienteCadastro.empresa_id || userEmpresa.id,
                // Endereço concatenado de clientes_cadastro
                endereco: enderecoCompleto,
                telefone: clienteCadastro.telefone || null,
                // Data de instalação (data atual)
                data_instalacao: new Date().toISOString().split('T')[0],
                // Tipo de serviço (da ordem de serviço - Instalação, Manutenção Corretiva, etc.)
                tipo_servico_id: ordemParaBaixa.id_tipo_servico || 'c381ce27-351e-456d-8188-9489c369a3a4',
                // Tipo de padrão (do formulário)
                tipo_padrao_id: baixaFormData.tipo_padrao_id || null,
                // Quantidade de módulos (do formulário)
                quantidade_modulos: baixaFormData.quantidade_modulos ? parseInt(baixaFormData.quantidade_modulos) : null,
                // Equipe da ordem de serviço
                equipe_id: ordemParaBaixa.id_equipe || null,
                // Campos da baixa (do formulário) - essenciais
                id_status: statusData.id,
                configuracao_inversor: baixaFormData.configuracao_inversor || false,
                nota_material: baixaFormData.nota_material || false
              }

              // Adicionar valor_material se o checkbox nota_material estiver marcado
              console.log('🔍 Verificando valor_material (insert):', {
                nota_material: baixaFormData.nota_material,
                valor_material: baixaFormData.valor_material,
                tipo: typeof baixaFormData.valor_material
              })
              
              if (baixaFormData.nota_material) {
                if (baixaFormData.valor_material && baixaFormData.valor_material.toString().trim() !== '') {
                  // Remover formatação e converter vírgula para ponto
                  const valorLimpo = baixaFormData.valor_material.toString().replace(/[^\d,.-]/g, '').replace(',', '.')
                  const valor = parseFloat(valorLimpo)
                  console.log('🔢 Conversão do valor (insert):', {
                    original: baixaFormData.valor_material,
                    limpo: valorLimpo,
                    convertido: valor,
                    isNaN: isNaN(valor)
                  })
                  
                  if (!isNaN(valor) && valor > 0) {
                    insertClienteData.valor_material = valor
                    console.log('✅ valor_material adicionado ao insert:', valor)
                  } else {
                    console.warn('⚠️ valor_material não é um número válido ou é zero:', baixaFormData.valor_material, '->', valor)
                  }
                } else {
                  console.warn('⚠️ nota_material marcado mas valor_material está vazio ou inválido')
                }
              }

              // Valor da Obra (independente do checkbox de nota de material)
              const valorObraInsert = parseValorMascarado(baixaFormData.valor_obra)
              if (valorObraInsert != null) {
                insertClienteData.valor_obra = valorObraInsert
              }
              
              console.log('📋 Dados para insert em clientes:', insertClienteData)

              // Inserir novo registro na tabela clientes
              const { error: insertClientesError } = await supabase
                .from('clientes')
                .insert([insertClienteData])

              if (insertClientesError) {
                console.error('Erro ao inserir na tabela clientes:', insertClientesError)
                console.error('Dados tentados:', insertClienteData)
                erroAoSalvarClientes = true
                mensagemErro = 'Erro ao inserir na tabela clientes: ' + insertClientesError.message
              } else {
                console.log('✅ Novo registro inserido na tabela clientes com sucesso')
              }
            }
          }
        }

        // Se houve erro ao salvar em clientes, mostrar modal de erro
        if (erroAoSalvarClientes) {
          setSuccessMessage('Erro ao salvar informações na tabela clientes: ' + mensagemErro)
          setShowSuccessModal(true)
          setIsLoadingBaixa(false)
          return
        }
      }

      // E-mail de confirmação da baixa (mesma função/Edge Function da tela de Vistorias):
      // destinatário = usuário logado; cópia (CC) = demais responsáveis da OS.
      let avisoEmail = ''
      if (userId) {
        try {
          const clienteNomeEmail =
            ordemParaBaixa?.cliente?.nome_completo ||
            (Array.isArray(ordemParaBaixa?.cliente) ? ordemParaBaixa.cliente[0]?.nome_completo : '') ||
            ''
          const tipoPadraoNome = (tiposPadrao || []).find((t) => String(t.id) === String(baixaFormData.tipo_padrao_id))?.nome || null
          const emailResult = await enviarEmailBaixaVistoria({
            id_usuario: userId,
            contexto: 'instalacao',
            tipo_servico: ordemParaBaixa?.tipo_servico?.nome || tipoFormulario || null,
            cliente_nome: clienteNomeEmail,
            ordem_id: ordemParaBaixa?.id || null,
            atualizacao: !!baixaExistenteId,
            status: 'Finalizado',
            tipo_padrao: tipoPadraoNome,
            observacoes: baixaFormData.observacoes || null,
            fotos: montarFotosBaixaEmail(fotosOrganizadas)
          })
          if (!emailResult.success) {
            console.warn('Baixa salva, mas falha ao enviar e-mail:', emailResult.error)
            avisoEmail = ' (Atenção: não foi possível enviar o e-mail de confirmação.)'
          }
        } catch (emailErr) {
          console.warn('Falha ao enviar e-mail de confirmação da baixa:', emailErr)
          avisoEmail = ' (Atenção: não foi possível enviar o e-mail de confirmação.)'
        }
      }

      setSuccessMessage((baixaExistenteId ? 'Formulário de baixa atualizado com sucesso!' : 'Formulário de baixa salvo com sucesso! Status atualizado para "Finalizado".') + avisoEmail)
      setShowSuccessModal(true)
      handleCloseBaixaModal()
      // Recarregar ordens para atualizar a lista
      await loadOrdensServico()
    } catch (error) {
      console.error('Erro ao salvar baixa:', error)
      alert('Erro ao salvar baixa: ' + error.message)
    } finally {
      setIsLoadingBaixa(false)
    }
  }

  // Salvar presença
  const handleSavePresenca = async (e) => {
    e.preventDefault()
    
    if (!ordemSelecionada) return

    // Validações
    if (!presencaFormData.veiculo_id) {
      alert('Por favor, selecione um carro/veículo.')
      return
    }

    if (!presencaFormData.colaboradores || presencaFormData.colaboradores.length === 0) {
      alert('Por favor, selecione pelo menos um colaborador.')
      return
    }

    try {
      setIsLoadingPresenca(true)

      // Preparar dados para salvar
      const dadosParaSalvar = {
        data_presenca: presencaFormData.data_presenca,
        data_cadastro_preenchido: presencaFormData.data_cadastro_preenchido,
        cliente_id: ordemSelecionada.id_cliente,
        equipe_id: presencaFormData.equipe_id || null,
        veiculo_id: presencaFormData.veiculo_id,
        km_inicial: presencaFormData.km_inicial || null,
        observacoes: presencaFormData.observacoes || null,
        id_os: ordemSelecionada.id // Vincular à ordem de serviço
      }

      // Criar presença
      const { data: presencaData, error: presencaError } = await supabase
        .from('presenca')
        .insert([dadosParaSalvar])
        .select('id')
      
      if (presencaError) throw presencaError

      const presencaId = presencaData[0].id

      // Salvar colaboradores na tabela presenca_colaboradores
      if (presencaId && presencaFormData.colaboradores.length > 0) {
        const colaboradoresData = presencaFormData.colaboradores.map(colaboradorId => ({
          presenca_id: presencaId,
          colaborador_id: colaboradorId,
          presente: true,
          observacoes: 'Presente'
        }))
        
        const { error: colaboradoresError } = await supabase
          .from('presenca_colaboradores')
          .insert(colaboradoresData)
        
        if (colaboradoresError) throw colaboradoresError
      }

      // Atualizar status da ordem de serviço para "Em andamento"
      const { error: updateError } = await supabase
        .from('ordens_servicos')
        .update({ status: 'Em andamento' })
        .eq('id', ordemSelecionada.id)
      
      if (updateError) {
        console.error('Erro ao atualizar status:', updateError)
        setSuccessMessage('Lista de presença salva, mas houve um erro ao atualizar o status da ordem.')
        setShowSuccessModal(true)
      } else {
        setSuccessMessage('Lista de presença salva com sucesso! Status atualizado para "Em andamento".')
        setShowSuccessModal(true)
      }
      
      handleClosePresencaModal()
      // Recarregar ordens para atualizar a lista
      await loadOrdensServico()
    } catch (error) {
      console.error('Erro ao salvar presença:', error)
      alert('Erro ao salvar presença: ' + error.message)
    } finally {
      setIsLoadingPresenca(false)
    }
  }

  return (
    <div className="container-fluid py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">⚡ Gestão Instalação</h2>
          <p className="text-muted mb-0">Gerencie e baixe ordens de serviço de todos os tipos (Instalação, Manutenção, Corretiva, Manutenção Corretiva, Troca de Inversor, Retorno, Obra Cívil e demais)</p>
        </div>
        {onBack && (
          <button 
            onClick={onBack} 
            className="btn btn-outline-secondary"
          >
            ← Voltar
          </button>
        )}
      </div>

      {/* Card de Filtros (recolhível) */}
      <div className="card mb-4 shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">🔍 Filtros</h5>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setShowFiltros(!showFiltros)}
            aria-expanded={showFiltros}
          >
            {showFiltros ? '▲ Ocultar filtros' : '▼ Mostrar filtros'}
          </button>
        </div>
        {showFiltros && (
        <div className="card-body">
          <div className="row g-3">
            <div className="col-12 col-md-6 col-lg-3">
              <label htmlFor="filtro-cliente" className="form-label">Cliente</label>
              <input
                type="text"
                id="filtro-cliente"
                name="cliente"
                value={filtros.cliente}
                onChange={handleFilterChange}
                placeholder="Buscar por cliente..."
                className="form-control"
              />
            </div>
            
            <div className="col-12 col-md-6 col-lg-3">
              <label className="form-label">Status</label>
              <div className="dropdown">
                <button
                  type="button"
                  className="form-select form-control text-start d-flex align-items-center justify-content-between"
                  onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                  aria-expanded={showStatusDropdown}
                  aria-haspopup="listbox"
                  id="filtro-status"
                >
                  <span className="text-truncate">
                    {statusSelecionados.length === 0
                      ? 'Todos'
                      : statusSelecionados.length === STATUS_OPCOES.length
                        ? 'Todos os status'
                        : `${statusSelecionados.length} selecionado(s)`}
                  </span>
                  <span className="dropdown-toggle ms-1" style={{ border: 'none', background: 'none' }} />
                </button>
                <ul
                  className={`dropdown-menu ${showStatusDropdown ? 'show' : ''}`}
                  style={{ maxHeight: 280, overflowY: 'auto', minWidth: '100%', zIndex: 1040 }}
                  role="listbox"
                >
                  <li className="dropdown-item py-1">
                    <label className="d-flex align-items-center gap-2 mb-0 cursor-pointer">
                      <input
                        ref={statusTodosCheckRef}
                        type="checkbox"
                        checked={statusSelecionados.length === STATUS_OPCOES.length}
                        onChange={(e) => handleStatusSelectAll(e.target.checked)}
                        onClick={(e) => e.stopPropagation()}
                        className="form-check-input"
                      />
                      <span className="small fw-bold">Todos</span>
                    </label>
                  </li>
                  <li><hr className="dropdown-divider my-1" /></li>
                  {STATUS_OPCOES.map((status) => (
                    <li key={status} className="dropdown-item py-1">
                      <label className="d-flex align-items-center gap-2 mb-0 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={statusSelecionados.includes(status)}
                          onChange={() => handleStatusToggle(status)}
                          onClick={(e) => e.stopPropagation()}
                          className="form-check-input"
                        />
                        <span className="small">{status}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </div>
              {showStatusDropdown && (
                <div
                  className="position-fixed"
                  style={{ top: 0, left: 0, right: 0, bottom: 0, zIndex: 1030 }}
                  onClick={() => setShowStatusDropdown(false)}
                  aria-hidden="true"
                />
              )}
            </div>
            
            <div className="col-12 col-md-6 col-lg-3">
              <label htmlFor="filtro-data-inicio" className="form-label">Data Início</label>
              <input
                type="date"
                id="filtro-data-inicio"
                name="dataInicio"
                value={filtros.dataInicio}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>
            
            <div className="col-12 col-md-6 col-lg-3">
              <label htmlFor="filtro-data-fim" className="form-label">Data Fim</label>
              <input
                type="date"
                id="filtro-data-fim"
                name="dataFim"
                value={filtros.dataFim}
                onChange={handleFilterChange}
                className="form-control"
              />
            </div>
          </div>
        </div>
        )}
      </div>

      {/* Card de Resultados */}
      <div className="card shadow-sm">
        <div className="card-header bg-light d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">📋 Ordens de Serviço</h5>
          <span className="badge bg-primary">
            {ordensFiltradas.length} {ordensFiltradas.length === 1 ? 'ordem' : 'ordens'}
          </span>
        </div>
        <div className="card-body">
          {(!idTiposGestao || idTiposGestao.length === 0) && !isLoadingOrdens ? (
            <div className="alert alert-warning" role="alert">
              <h6 className="alert-heading">⚠️ Tipos de serviço não encontrados</h6>
              <p className="mb-0">
                Verifique se existem os tipos de serviço: Instalação, Manutenção, Corretiva, Manutenção Corretiva, Troca de Inversor e Retorno na tabela tipo_servico.
              </p>
            </div>
          ) : isLoadingOrdens ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <p className="mt-3 text-muted">Carregando ordens de instalação...</p>
            </div>
          ) : ordensFiltradas.length === 0 ? (
            <div className="alert alert-info" role="alert">
              <h6 className="alert-heading">ℹ️ Nenhuma ordem encontrada</h6>
              <p className="mb-0">
                {ordensServico.length > 0 
                  ? `Nenhuma ordem corresponde aos filtros aplicados. Total de ordens: ${ordensServico.length}`
                  : 'Nenhuma ordem de instalação cadastrada ainda.'}
              </p>
            </div>
          ) : (
            <>
              {/* Desktop: tabela */}
              <div className="gestao-instalacao-table-wrap table-responsive">
                <table className="table table-hover table-striped align-middle">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">Cliente</th>
                      <th scope="col">Tipo de Serviço</th>
                      <th scope="col">Status</th>
                      <th scope="col">Data Execução</th>
                      <th scope="col">Data Criação</th>
                      <th scope="col" className="text-center">Presença</th>
                      <th scope="col">Empresa</th>
                      <th scope="col" className="text-center">Anexos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ordensFiltradas.map(ordem => (
                      <tr key={ordem.id}>
                        <td>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault()
                              setClienteOpcoesOrdem(ordem)
                              setShowClienteOpcoesModal(true)
                            }}
                            className="text-primary text-decoration-none fw-bold"
                            style={{ cursor: 'pointer' }}
                            title="Clique para ver opções do cliente"
                            onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                          >
                            {ordem.cliente?.nome_completo || 'N/A'}
                          </a>
                        </td>
                        <td>
                          <span className="badge bg-info">⚡ {ordem.tipo_servico?.nome || 'N/A'}</span>
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(ordem.status)}`}>
                            {ordem.status || 'N/A'}
                          </span>
                          {ordem.medicao_fechada && (
                            <span className="badge bg-secondary ms-1" title="Medição fechada em Serviços">🔒 Medição fechada</span>
                          )}
                        </td>
                        <td>{formatarDataSemHora(ordem.data_execucao)}</td>
                        <td>{formatarDataSemHora(ordem.data_criacao || ordem.created_at)}</td>
                        <td className="text-center">
                          {ordem.presenca ? (
                            <span className="badge bg-success">✓ Sim</span>
                          ) : (
                            <span className="badge bg-secondary">✗ Não</span>
                          )}
                        </td>
                        <td>
                          <small className="text-muted">{ordem.empresa?.empresa || 'N/A'}</small>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              setPdfsModalData({
                                id_cliente: ordem.id_cliente,
                                nome_completo: ordem.cliente?.nome_completo || 'Cliente'
                              })
                              setShowPdfsModal(true)
                            }}
                            title="Visualizar PDFs anexados ao cliente"
                          >
                            📄 Ver PDFs
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile: cards */}
              <div className="gestao-instalacao-cards">
                {ordensFiltradas.map(ordem => {
                  const obraIniciada = ordem.status && ordem.status !== 'Novo'
                  return (
                    <div key={ordem.id} className="gestao-instalacao-card">
                      <div className="gestao-instalacao-card-tipo">
                        <span className="badge bg-info">⚡ {ordem.tipo_servico?.nome || 'N/A'}</span>
                      </div>
                      <div className="gestao-instalacao-card-cliente">
                        <button
                          type="button"
                          className="btn-link-cliente"
                          onClick={() => {
                            setClienteOpcoesOrdem(ordem)
                            setShowClienteOpcoesModal(true)
                          }}
                        >
                          {ordem.cliente?.nome_completo || 'N/A'}
                        </button>
                      </div>
                      <div className="gestao-instalacao-card-status">
                        <span className={`badge ${getStatusBadgeClass(ordem.status)}`}>
                          {ordem.status || 'N/A'}
                        </span>
                        {ordem.medicao_fechada && (
                          <span className="badge bg-secondary" title="Medição fechada em Serviços">🔒 Medição fechada</span>
                        )}
                      </div>
                      <div className="gestao-instalacao-card-datas">
                        {formatarDataSemHora(ordem.data_execucao)} | {formatarDataSemHora(ordem.data_criacao || ordem.created_at)}
                      </div>
                      <div className="gestao-instalacao-card-presenca">
                        {ordem.presenca ? (
                          <span className="badge bg-success">✓ Presença</span>
                        ) : (
                          <span className="badge bg-secondary">✗ Presença</span>
                        )}
                      </div>
                      <div className="gestao-instalacao-card-acoes">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => {
                            setPdfsModalData({
                              id_cliente: ordem.id_cliente,
                              nome_completo: ordem.cliente?.nome_completo || 'Cliente'
                            })
                            setShowPdfsModal(true)
                          }}
                          title="PDFs"
                        >
                          📄 PDFs
                        </button>
                        {obraIniciada && !ordem.medicao_fechada && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => handleOpenBaixaModal(ordem)}
                            title="Baixar"
                          >
                            📥 Baixar
                          </button>
                        )}
                        {obraIniciada && !ordem.medicao_fechada && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => handleOpenBaixaModal(ordem)}
                            title="Editar"
                          >
                            ✏️ Editar
                          </button>
                        )}
                        {ordem.medicao_fechada && (ordem.status === 'Finalizado' || ordem.status === 'Validado') && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleOpenVisualizarBaixa(ordem)}
                            title="Consultar baixa"
                          >
                            📋 Consultar
                          </button>
                        )}
                        {!obraIniciada && ordem.status === 'Novo' && (
                          <button
                            type="button"
                            className="btn btn-sm btn-primary"
                            onClick={() => handleOpenPresencaModal(ordem)}
                            title="Iniciar Obra"
                          >
                            ▶️ Iniciar Obra
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal de opções ao clicar no cliente: 1) Info endereço/kit/fachada/mapa  2) Baixar/Editar ou Consultar */}
      {showClienteOpcoesModal && clienteOpcoesOrdem && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
          tabIndex="-1"
          onClick={() => {
            setShowClienteOpcoesModal(false)
            setClienteOpcoesOrdem(null)
          }}
        >
          <div
            className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  👤 {clienteOpcoesOrdem.cliente?.nome_completo || 'Cliente'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Fechar"
                  onClick={() => {
                    setShowClienteOpcoesModal(false)
                    setClienteOpcoesOrdem(null)
                  }}
                />
              </div>
              <div className="modal-body">
                {/* Opção 1: Informações do endereço, kit solar, fachada e mapa */}
                <div className="mb-4">
                  <h6 className="border-bottom pb-2 mb-3">1️⃣ Informações do cliente</h6>
                  <div className="row g-2">
                    {/* Endereço */}
                    <div className="col-12">
                      <strong className="text-muted small">Endereço</strong>
                      <p className="mb-0">
                        {[
                          clienteOpcoesOrdem.cliente?.logradouro,
                          clienteOpcoesOrdem.cliente?.numero,
                          clienteOpcoesOrdem.cliente?.complemento,
                          [clienteOpcoesOrdem.cliente?.bairro, clienteOpcoesOrdem.cliente?.cidade, clienteOpcoesOrdem.cliente?.estado].filter(Boolean).join(' – '),
                          clienteOpcoesOrdem.cliente?.cep ? `CEP ${clienteOpcoesOrdem.cliente.cep.replace(/(\d{5})(\d{3})/, '$1-$2')}` : null
                        ].filter(Boolean).join(', ') || '—'}
                      </p>
                    </div>
                    {/* Kit solar */}
                    {clienteOpcoesOrdem.kit_solar && (
                      <div className="col-12">
                        <strong className="text-muted small">Kit solar</strong>
                        <p className="mb-0">
                          Inversor: {clienteOpcoesOrdem.kit_solar.marca_inversor || '—'} {clienteOpcoesOrdem.kit_solar.potencia_inversor != null && `${clienteOpcoesOrdem.kit_solar.potencia_inversor} kW`} {clienteOpcoesOrdem.kit_solar.quantidade_inversor > 1 && `(${clienteOpcoesOrdem.kit_solar.quantidade_inversor}x)`}
                          {' · '}
                          Módulo: {clienteOpcoesOrdem.kit_solar.marca_modulo || '—'} {clienteOpcoesOrdem.kit_solar.potencia_modulo != null && `${clienteOpcoesOrdem.kit_solar.potencia_modulo} W`} {clienteOpcoesOrdem.kit_solar.quantidade_modulo > 1 && `(${clienteOpcoesOrdem.kit_solar.quantidade_modulo}x)`}
                        </p>
                      </div>
                    )}
                    {/* Fachada */}
                    {clienteOpcoesOrdem.cliente?.foto_fachada && (
                      <div className="col-12">
                        <strong className="text-muted small d-block mb-1">Fachada</strong>
                        <a href={clienteOpcoesOrdem.cliente.foto_fachada} target="_blank" rel="noopener noreferrer" className="d-inline-block">
                          <img
                            src={clienteOpcoesOrdem.cliente.foto_fachada}
                            alt="Fachada"
                            style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain', border: '1px solid #dee2e6', borderRadius: 8 }}
                          />
                        </a>
                      </div>
                    )}
                    {/* Link do mapa */}
                    {clienteOpcoesOrdem.cliente?.link_localizacao && (
                      <div className="col-12">
                        <a
                          href={clienteOpcoesOrdem.cliente.link_localizacao}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                        >
                          🗺️ Abrir no mapa
                        </a>
                      </div>
                    )}
                  </div>
                </div>
                {/* Opção 2: Baixar/Editar ou Consultar conforme status */}
                <div>
                  <h6 className="border-bottom pb-2 mb-3">2️⃣ Ação conforme status</h6>
                  <div className="d-flex flex-wrap gap-2">
                    {clienteOpcoesOrdem.status === 'Novo' && (
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={() => {
                          setShowClienteOpcoesModal(false)
                          setClienteOpcoesOrdem(null)
                          handleOpenPresencaModal(clienteOpcoesOrdem)
                        }}
                      >
                        {clienteOpcoesOrdem.presenca ? '📋 Preencher lista de presença' : '▶️ Iniciar serviço'}
                      </button>
                    )}
                    {clienteOpcoesOrdem.status === 'Em andamento' && !clienteOpcoesOrdem.medicao_fechada && (
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={() => {
                          setShowClienteOpcoesModal(false)
                          setClienteOpcoesOrdem(null)
                          handleOpenBaixaModal(clienteOpcoesOrdem)
                        }}
                      >
                        📥 Baixar / Editar
                      </button>
                    )}
                    {clienteOpcoesOrdem.status === 'Em andamento' && clienteOpcoesOrdem.medicao_fechada && (
                      <span className="text-muted small align-self-center">Medição fechada. Não é possível editar a baixa.</span>
                    )}
                    {clienteOpcoesOrdem.status === 'Finalizado' && (
                      <>
                        {!clienteOpcoesOrdem.medicao_fechada && (
                          <button
                            type="button"
                            className="btn btn-warning"
                            onClick={() => {
                              setShowClienteOpcoesModal(false)
                              setClienteOpcoesOrdem(null)
                              handleOpenBaixaModal(clienteOpcoesOrdem)
                            }}
                          >
                            📥 Editar
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-success"
                          onClick={() => {
                            setShowClienteOpcoesModal(false)
                            setClienteOpcoesOrdem(null)
                            handleOpenVisualizarBaixa(clienteOpcoesOrdem)
                          }}
                        >
                          📋 Consultar
                        </button>
                        {clienteOpcoesOrdem.medicao_fechada && (
                          <span className="text-muted small align-self-center">Medição fechada. Edição bloqueada.</span>
                        )}
                      </>
                    )}
                    {clienteOpcoesOrdem.status === 'Validado' && (
                      <button
                        type="button"
                        className="btn btn-success"
                        onClick={() => {
                          setShowClienteOpcoesModal(false)
                          setClienteOpcoesOrdem(null)
                          handleOpenVisualizarBaixa(clienteOpcoesOrdem)
                        }}
                      >
                        📋 Consultar
                      </button>
                    )}
                    {clienteOpcoesOrdem.status === 'Cancelado' && (
                      <span className="text-muted small align-self-center">Ordem cancelada.</span>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowClienteOpcoesModal(false)
                    setClienteOpcoesOrdem(null)
                  }}
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Presença - Renderizado via Portal diretamente no body */}
      {showPresencaModal && ordemSelecionada && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="modal-backdrop fade show" 
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              margin: 0,
              padding: 0
            }}
            onClick={handleClosePresencaModal}
          ></div>
          
          {/* Modal */}
          <div 
            className="modal fade show" 
            style={{ 
              display: 'flex',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1060,
              width: '100vw',
              height: '100vh',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              padding: '1rem',
              margin: 0,
              backgroundColor: 'transparent',
              transform: 'none',
              opacity: 1,
              visibility: 'visible',
              pointerEvents: 'auto',
              boxSizing: 'border-box'
            }} 
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              // Fechar ao clicar fora do modal-content
              if (e.target === e.currentTarget) {
                handleClosePresencaModal()
              }
            }}
          >
            <div 
              className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered"
              style={{
                position: 'relative',
                display: 'block',
                width: 'auto',
                margin: 0,
                maxHeight: '90vh',
                transform: 'none',
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'auto',
                alignSelf: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div 
                className="modal-content"
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  maxHeight: '90vh',
                  pointerEvents: 'auto',
                  backgroundColor: '#ffffff',
                  backgroundClip: 'padding-box',
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  borderRadius: '0.5rem',
                  boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                  outline: 0,
                  overflow: 'hidden',
                  margin: 0,
                  transform: 'none',
                  opacity: 1,
                  visibility: 'visible'
                }}
              >
                <div 
                  className="modal-header"
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem 1rem',
                    borderBottom: '1px solid #dee2e6',
                    borderTopLeftRadius: 'calc(0.5rem - 1px)',
                    borderTopRightRadius: 'calc(0.5rem - 1px)'
                  }}
                >
                  <h5 className="modal-title" style={{ margin: 0, lineHeight: 1.5 }}>
                    📋 Lista de Presença - {ordemSelecionada.cliente?.nome_completo || 'N/A'}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={handleClosePresencaModal}
                    aria-label="Fechar"
                    style={{
                      padding: '0.5rem 0.5rem',
                      margin: '-0.5rem -0.5rem -0.5rem auto',
                      cursor: 'pointer',
                      border: 0,
                      borderRadius: '0.375rem',
                      opacity: 0.5,
                      backgroundColor: 'transparent',
                      backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\' fill=\'%23000\'%3e%3cpath d=\'M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z\'/%3e%3c/svg%3e")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      backgroundSize: '1em',
                      width: '1em',
                      height: '1em'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.75'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.5'}
                  ></button>
                </div>
              <form onSubmit={handleSavePresenca} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div className="modal-body" style={{ overflowY: 'auto', flex: '1 1 auto' }}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="data_presenca" className="form-label">Data da Presença *</label>
                      <input
                        type="date"
                        id="data_presenca"
                        className="form-control"
                        value={presencaFormData.data_presenca}
                        onChange={(e) => setPresencaFormData({...presencaFormData, data_presenca: e.target.value})}
                        required
                      />
                    </div>
                    
                    <div className="col-md-6">
                      <label htmlFor="equipe_id" className="form-label">Equipe</label>
                      <select
                        id="equipe_id"
                        className="form-select"
                        value={presencaFormData.equipe_id}
                        onChange={(e) => setPresencaFormData({...presencaFormData, equipe_id: e.target.value})}
                      >
                        <option value="">Selecione uma equipe (opcional)</option>
                        {equipes.map(equipe => (
                          <option key={equipe.id} value={equipe.id}>
                            {equipe.nome}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6">
                      <label htmlFor="veiculo_id" className="form-label">Carro/Veículo *</label>
                      <select
                        id="veiculo_id"
                        className="form-select"
                        value={presencaFormData.veiculo_id}
                        onChange={(e) => setPresencaFormData({...presencaFormData, veiculo_id: e.target.value})}
                        required
                      >
                        <option value="">Selecione um carro/veículo</option>
                        {veiculos.map(veiculo => (
                          <option key={veiculo.id} value={veiculo.id}>
                            {veiculo.veiculo} - {veiculo.placa}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div className="col-md-6">
                      <label htmlFor="km_inicial" className="form-label">KM Inicial</label>
                      <input
                        type="number"
                        id="km_inicial"
                        className="form-control"
                        value={presencaFormData.km_inicial}
                        onChange={(e) => setPresencaFormData({...presencaFormData, km_inicial: e.target.value})}
                        placeholder="Ex: 15000"
                        min="0"
                        step="1"
                      />
                    </div>
                    
                    <div className="col-12">
                      <label className="form-label">Colaboradores *</label>
                      <div className="mb-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary me-2"
                          onClick={() => {
                            const todosAtivos = colaboradores.map(c => c.id)
                            setPresencaFormData({
                              ...presencaFormData,
                              colaboradores: todosAtivos
                            })
                          }}
                        >
                          ✅ Selecionar Todos
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => {
                            setPresencaFormData({
                              ...presencaFormData,
                              colaboradores: []
                            })
                          }}
                        >
                          🗑️ Limpar Seleção
                        </button>
                      </div>
                      <div className="border rounded p-3" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        <div className="row g-2">
                          {colaboradores.map((colaborador) => (
                            <div key={colaborador.id} className="col-md-6">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  id={`colab-${colaborador.id}`}
                                  checked={presencaFormData.colaboradores.includes(colaborador.id)}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setPresencaFormData({
                                        ...presencaFormData,
                                        colaboradores: [...presencaFormData.colaboradores, colaborador.id]
                                      })
                                    } else {
                                      setPresencaFormData({
                                        ...presencaFormData,
                                        colaboradores: presencaFormData.colaboradores.filter(id => id !== colaborador.id)
                                      })
                                    }
                                  }}
                                />
                                <label className="form-check-label" htmlFor={`colab-${colaborador.id}`}>
                                  <strong>{colaborador.nome}</strong>
                                  {colaborador.cargo && <small className="text-muted d-block">{colaborador.cargo}</small>}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <small className="text-muted">
                        {presencaFormData.colaboradores.length} de {colaboradores.length} colaboradores selecionados
                      </small>
                    </div>
                    
                    <div className="col-12">
                      <label htmlFor="observacoes" className="form-label">Observações</label>
                      <textarea
                        id="observacoes"
                        className="form-control"
                        rows="3"
                        value={presencaFormData.observacoes}
                        onChange={(e) => setPresencaFormData({...presencaFormData, observacoes: e.target.value})}
                        placeholder="Digite observações sobre a presença..."
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleClosePresencaModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={isLoadingPresenca}>
                    {isLoadingPresenca ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Salvando...
                      </>
                    ) : (
                      '💾 Salvar Presença'
                    )}
                  </button>
                </div>
              </form>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de Confirmação de Início de Serviço */}
      {showConfirmInicioModal && ordemParaIniciar && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="modal-backdrop fade show" 
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              margin: 0,
              padding: 0
            }}
            onClick={handleCloseConfirmInicioModal}
          ></div>
          
          {/* Modal */}
          <div 
            className="modal fade show" 
            style={{ 
              display: 'flex',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1060,
              width: '100vw',
              height: '100vh',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              padding: '1rem',
              margin: 0,
              backgroundColor: 'transparent',
              transform: 'none',
              opacity: 1,
              visibility: 'visible',
              pointerEvents: 'auto',
              boxSizing: 'border-box'
            }} 
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseConfirmInicioModal()
              }
            }}
          >
            <div 
              className="modal-dialog modal-dialog-centered"
              style={{
                position: 'relative',
                display: 'block',
                width: 'auto',
                margin: 0,
                maxHeight: '90vh',
                transform: 'none',
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'auto',
                alignSelf: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div 
                className="modal-content"
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  maxWidth: '500px',
                  maxHeight: '90vh',
                  pointerEvents: 'auto',
                  backgroundColor: '#ffffff',
                  backgroundClip: 'padding-box',
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  borderRadius: '0.5rem',
                  boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                  outline: 0,
                  overflow: 'hidden',
                  margin: 0,
                  transform: 'none',
                  opacity: 1,
                  visibility: 'visible'
                }}
              >
                <div 
                  className="modal-header"
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem 1rem',
                    borderBottom: '1px solid #dee2e6',
                    borderTopLeftRadius: 'calc(0.5rem - 1px)',
                    borderTopRightRadius: 'calc(0.5rem - 1px)'
                  }}
                >
                  <h5 className="modal-title" style={{ margin: 0, lineHeight: 1.5 }}>
                    ⚡ Iniciar Serviço
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={handleCloseConfirmInicioModal}
                    aria-label="Fechar"
                    style={{
                      padding: '0.5rem 0.5rem',
                      margin: '-0.5rem -0.5rem -0.5rem auto',
                      cursor: 'pointer',
                      border: 0,
                      borderRadius: '0.375rem',
                      opacity: 0.5,
                      backgroundColor: 'transparent',
                      backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\' fill=\'%23000\'%3e%3cpath d=\'M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z\'/%3e%3c/svg%3e")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      backgroundSize: '1em',
                      width: '1em',
                      height: '1em'
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.75'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.5'}
                  ></button>
                </div>
                <div className="modal-body" style={{ padding: '1.5rem' }}>
                  <p className="mb-3">
                    Deseja iniciar o serviço de instalação para o cliente <strong>{ordemParaIniciar?.cliente?.nome_completo || 'N/A'}</strong>?
                  </p>
                  <p className="text-muted mb-0">
                    O status da ordem será alterado para <strong>"Em andamento"</strong>.
                  </p>
                </div>
                <div className="modal-footer" style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: '0.5rem',
                  padding: '1rem',
                  borderTop: '1px solid #dee2e6'
                }}>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleCloseConfirmInicioModal}
                    disabled={isLoadingInicio}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary" 
                    onClick={handleConfirmarInicioServico}
                    disabled={isLoadingInicio}
                  >
                    {isLoadingInicio ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Iniciando...
                      </>
                    ) : (
                      '✅ Confirmar Início'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de Sucesso */}
      {showSuccessModal && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="modal-backdrop fade show" 
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              margin: 0,
              padding: 0
            }}
            onClick={() => setShowSuccessModal(false)}
          ></div>
          
          {/* Modal */}
          <div 
            className="modal fade show" 
            style={{ 
              display: 'flex',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1060,
              width: '100vw',
              height: '100vh',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              padding: '1rem',
              margin: 0,
              backgroundColor: 'transparent',
              transform: 'none',
              opacity: 1,
              visibility: 'visible',
              pointerEvents: 'auto',
              boxSizing: 'border-box'
            }} 
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowSuccessModal(false)
              }
            }}
          >
            <div 
              className="modal-dialog modal-dialog-centered"
              style={{
                position: 'relative',
                display: 'block',
                width: 'auto',
                margin: 0,
                maxHeight: '90vh',
                transform: 'none',
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'auto',
                alignSelf: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div 
                className="modal-content"
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  maxWidth: '500px',
                  maxHeight: '90vh',
                  pointerEvents: 'auto',
                  backgroundColor: '#ffffff',
                  backgroundClip: 'padding-box',
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  borderRadius: '0.5rem',
                  boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                  outline: 0,
                  overflow: 'hidden',
                  margin: 0,
                  transform: 'none',
                  opacity: 1,
                  visibility: 'visible'
                }}
              >
                <div 
                  className="modal-header"
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '1.5rem 1rem',
                    borderBottom: 'none',
                    background: successMessage.toLowerCase().includes('erro') 
                      ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
                      : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    borderTopLeftRadius: 'calc(0.5rem - 1px)',
                    borderTopRightRadius: 'calc(0.5rem - 1px)'
                  }}
                >
                  <div style={{ 
                    width: '64px', 
                    height: '64px', 
                    borderRadius: '50%', 
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '2rem'
                  }}>
                    {successMessage.toLowerCase().includes('erro') ? '❌' : '✅'}
                  </div>
                </div>
                <div className="modal-body" style={{ 
                  padding: '2rem 1.5rem',
                  textAlign: 'center'
                }}>
                  <h5 style={{ 
                    marginBottom: '1rem',
                    color: '#1f2937',
                    fontWeight: '600',
                    fontSize: '1.25rem'
                  }}>
                    {successMessage.toLowerCase().includes('erro') ? 'Erro!' : 'Sucesso!'}
                  </h5>
                  <p style={{ 
                    margin: 0,
                    color: '#6b7280',
                    fontSize: '1rem',
                    lineHeight: '1.5'
                  }}>
                    {successMessage}
                  </p>
                </div>
                <div className="modal-footer" style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  padding: '1rem 1.5rem 1.5rem',
                  borderTop: 'none'
                }}>
                  <button 
                    type="button" 
                    className={successMessage.toLowerCase().includes('erro') ? 'btn btn-danger' : 'btn btn-success'}
                    onClick={() => setShowSuccessModal(false)}
                    style={{
                      padding: '0.75rem 2rem',
                      fontSize: '1rem',
                      fontWeight: '600',
                      borderRadius: '0.5rem',
                      border: 'none',
                      background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                      color: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'translateY(-2px)'
                      e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)'
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'translateY(0)'
                      e.target.style.boxShadow = 'none'
                    }}
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de Baixa de Instalação */}
      {showBaixaModal && ordemParaBaixa && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="modal-backdrop fade show" 
            style={{ 
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1050,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              margin: 0,
              padding: 0
            }}
            onClick={handleCloseBaixaModal}
          ></div>
          
          {/* Modal Formulário de Baixa */}
          <div 
            className="modal fade show modal-formulario-baixa" 
            style={{ 
              display: 'flex',
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1060,
              width: '100vw',
              height: '100vh',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
              padding: '1rem',
              margin: 0,
              backgroundColor: 'transparent',
              transform: 'none',
              opacity: 1,
              visibility: 'visible',
              pointerEvents: 'auto',
              boxSizing: 'border-box'
            }} 
            tabIndex="-1"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                handleCloseBaixaModal()
              }
            }}
          >
            <div 
              className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered modal-baixa"
              style={{
                position: 'relative',
                display: 'block',
                width: 'auto',
                margin: 0,
                maxHeight: '90vh',
                transform: 'none',
                opacity: 1,
                visibility: 'visible',
                pointerEvents: 'auto',
                alignSelf: 'center'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div 
                className="modal-content modal-content-baixa"
                style={{
                  position: 'relative',
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%',
                  maxWidth: '1200px',
                  maxHeight: '90vh',
                  pointerEvents: 'auto',
                  backgroundColor: '#ffffff',
                  backgroundClip: 'padding-box',
                  border: '1px solid rgba(0, 0, 0, 0.2)',
                  borderRadius: '0.5rem',
                  boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
                  outline: 0,
                  overflow: 'hidden',
                  margin: 0,
                  transform: 'none',
                  opacity: 1,
                  visibility: 'visible'
                }}
              >
                <div 
                  className="modal-header modal-header-baixa"
                  style={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '1rem 1rem',
                    borderBottom: '1px solid #dee2e6',
                    borderTopLeftRadius: 'calc(0.5rem - 1px)',
                    borderTopRightRadius: 'calc(0.5rem - 1px)'
                  }}
                >
                  <h5 className="modal-title modal-title-baixa" style={{ 
                    margin: 0, 
                    lineHeight: 1.5,
                    flex: 1,
                    minWidth: 0,
                    wordBreak: 'break-word'
                  }}>
                    📋 Formulário de Baixa - {ordemParaBaixa.cliente?.nome_completo || 'N/A'}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close btn-close-baixa" 
                    onClick={handleCloseBaixaModal}
                    aria-label="Fechar"
                    style={{
                      padding: '0.5rem',
                      margin: '-0.5rem -0.5rem -0.5rem auto',
                      cursor: 'pointer',
                      border: 0,
                      borderRadius: '0.375rem',
                      opacity: 0.5,
                      backgroundColor: 'transparent',
                      backgroundImage: 'url("data:image/svg+xml,%3csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 16 16\' fill=\'%23000\'%3e%3cpath d=\'M.293.293a1 1 0 0 1 1.414 0L8 6.586 14.293.293a1 1 0 1 1 1.414 1.414L9.414 8l6.293 6.293a1 1 0 0 1-1.414 1.414L8 9.414l-6.293 6.293a1 1 0 0 1-1.414-1.414L6.586 8 .293 1.707a1 1 0 0 1 0-1.414z\'/%3e%3c/svg%3e")',
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center',
                      backgroundSize: '1em',
                      width: '1em',
                      height: '1em',
                      flexShrink: 0
                    }}
                    onMouseEnter={(e) => e.target.style.opacity = '0.75'}
                    onMouseLeave={(e) => e.target.style.opacity = '0.5'}
                  ></button>
                </div>
                <form onSubmit={handleSaveBaixa} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <>
                  <div className="modal-body modal-body-baixa" style={{ 
                    overflowY: 'auto', 
                    flex: '1 1 auto', 
                    padding: '1.5rem',
                    WebkitOverflowScrolling: 'touch'
                  }}>
                    {isLoadingBaixaParaEdicao && (
                      <div className="text-center py-4 text-muted">
                        <div className="spinner-border mb-2" role="status" />
                        <p className="mb-0">Carregando dados para edição...</p>
                      </div>
                    )}
                    {!isLoadingBaixaParaEdicao && (
                    <div className="row g-3">
                      {ordemParaBaixa?.tipo_servico?.nome === 'Instalação' ? (
                      <>
                      {/* Seção: Informações Adicionais - no topo */}
                      <div className="col-12">
                        <h6 className="border-bottom pb-2 mb-3">💰 Informações Adicionais</h6>
                      </div>
                      <div className="col-12">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="configuracao_inversor"
                            checked={baixaFormData.configuracao_inversor}
                            onChange={(e) => setBaixaFormData({
                              ...baixaFormData,
                              configuracao_inversor: e.target.checked
                            })}
                          />
                          <label className="form-check-label" htmlFor="configuracao_inversor">
                            Configuração de Inversor
                          </label>
                        </div>
                      </div>
                      <div className="col-12">
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="nota_material"
                            checked={baixaFormData.nota_material}
                            onChange={(e) => setBaixaFormData({
                              ...baixaFormData,
                              nota_material: e.target.checked,
                              valor_material: e.target.checked ? baixaFormData.valor_material : ''
                            })}
                          />
                          <label className="form-check-label" htmlFor="nota_material">
                            Nota de Material
                          </label>
                        </div>
                        {baixaFormData.nota_material && (
                          <div className="ms-4 mb-3">
                            <label htmlFor="valor_material" className="form-label">Valor *</label>
                            <input
                              type="text"
                              id="valor_material"
                              className="form-control"
                              placeholder="0,00"
                              value={baixaFormData.valor_material}
                              onChange={(e) => {
                                const value = e.target.value.replace(/[^\d,.-]/g, '')
                                setBaixaFormData({...baixaFormData, valor_material: value})
                              }}
                              required
                            />
                          </div>
                        )}
                      </div>

                      {/* Informações Básicas - apenas para Instalação */}
                      <div className="col-12 mt-4">
                        <h6 className="border-bottom pb-2 mb-3">📋 Informações Básicas</h6>
                      </div>
                      
                      <div className="col-md-6">
                        <label htmlFor="tipo_padrao_id" className="form-label">Tipo de Padrão *</label>
                        <select
                          id="tipo_padrao_id"
                          className="form-select"
                          value={baixaFormData.tipo_padrao_id}
                          onChange={(e) => setBaixaFormData({...baixaFormData, tipo_padrao_id: e.target.value})}
                          required
                        >
                          <option value="">Selecione o tipo de padrão</option>
                          {tiposPadrao.map((tipo) => (
                            <option key={tipo.id} value={tipo.id}>
                              {tipo.nome}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="tipo_fase" className="form-label">Tipo de Fase do Padrão *</label>
                        <select
                          id="tipo_fase"
                          className="form-select"
                          value={baixaFormData.tipo_fase}
                          onChange={(e) => setBaixaFormData({...baixaFormData, tipo_fase: e.target.value})}
                          required
                        >
                          <option value="">Selecione o tipo de fase do padrão</option>
                          <option value="Monofásico">Monofásico</option>
                          <option value="Bifásico">Bifásico</option>
                          <option value="Trifásico">Trifásico</option>
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="quantidade_modulos" className="form-label">Qtd Módulos Instalados</label>
                        <input
                          type="number"
                          id="quantidade_modulos"
                          className="form-control"
                          min="0"
                          step="1"
                          value={baixaFormData.quantidade_modulos}
                          onChange={(e) => setBaixaFormData({...baixaFormData, quantidade_modulos: e.target.value})}
                          placeholder="0"
                        />
                      </div>

                      <div className="col-md-6">
                        <label htmlFor="tipo_inversor" className="form-label">Tipo de Inversor *</label>
                        <select
                          id="tipo_inversor"
                          className="form-select"
                          value={baixaFormData.tipo_inversor}
                          onChange={(e) => setBaixaFormData({...baixaFormData, tipo_inversor: e.target.value})}
                          required
                        >
                          <option value="">Selecione o tipo de inversor</option>
                          <option value="Bifásico">Bifásico</option>
                          <option value="Trifásico">Trifásico</option>
                        </select>
                      </div>

                      {/* Seção: Fotos Gerais */}
                      <div className="col-12 mt-4">
                        <h6 className="border-bottom pb-2 mb-3">📸 Fotos Gerais</h6>
                      </div>
                      {renderFotoField('foto_fachada', 'Foto da Fachada (com data e hora)', true, false, 'A foto será salva com data e hora no nome do arquivo.')}
                      {renderFotoField('foto_padrao_completo', 'Foto do Padrão Completo (com placa de geração)', true, false)}

                      {/* Seção: Fotos do Padrão */}
                      <div className="col-12 mt-4">
                        <h6 className="border-bottom pb-2 mb-3">⚡ Fotos do Padrão</h6>
                      </div>

                      {/* Fotos Condicionais do Padrão - Monofásico */}
                      {baixaFormData.tipo_fase === 'Monofásico' && (
                        <>
                          {renderFotoField('foto_fase1_neutro', 'Fase 1 e Neutro', true)}
                          {renderFotoField('foto_terra_neutro', 'Terra e Neutro', true)}
                        </>
                      )}

                      {/* Fotos Condicionais do Padrão - Bifásico */}
                      {baixaFormData.tipo_fase === 'Bifásico' && (
                        <>
                          {renderFotoField('foto_fase1_fase2', 'Fase 1 e Fase 2', true)}
                          {renderFotoField('foto_fase1_neutro', 'Fase 1 e Neutro', true)}
                          {renderFotoField('foto_fase2_neutro', 'Fase 2 e Neutro', true)}
                          {renderFotoField('foto_terra_neutro', 'Terra e Neutro', true)}
                        </>
                      )}

                      {/* Fotos Condicionais do Padrão - Trifásico */}
                      {baixaFormData.tipo_fase === 'Trifásico' && (
                        <>
                          {renderFotoField('foto_fase1_fase2', 'Fase 1 e Fase 2', true)}
                          {renderFotoField('foto_fase2_fase3', 'Fase 2 e Fase 3', true)}
                          {renderFotoField('foto_fase3_fase1', 'Fase 3 e Fase 1', true)}
                          {renderFotoField('foto_fase1_neutro', 'Fase 1 e Neutro', true)}
                          {renderFotoField('foto_fase2_neutro', 'Fase 2 e Neutro', true)}
                          {renderFotoField('foto_fase3_neutro', 'Fase 3 e Neutro', true)}
                          {renderFotoField('foto_terra_neutro', 'Terra e Neutro', true)}
                        </>
                      )}

                      {/* Fotos do Padrão (Sempre) */}
                      {renderFotoField('foto_disjuntor_padrao', 'Foto do Disjuntor do Padrão', true)}
                      {renderFotoField('foto_aterramento_padrao', 'Foto do Aterramento do Padrão', true)}
                      {renderFotoField('foto_conexoes_padrao', 'Foto das Conexões do Padrão (perfurantes)', true, true, 'Você pode adicionar múltiplas imagens.')}

                      {/* Seção: Fotos do Inversor e QPCA */}
                      <div className="col-12 mt-4">
                        <h6 className="border-bottom pb-2 mb-3">🔌 Fotos do Inversor e QPCA</h6>
                      </div>

                      {/* Fotos do Inversor (Sempre) */}
                      {renderFotoField('foto_instalacao_inversor', 'Foto da Instalação do Inversor', true)}
                      {renderFotoField('foto_qpca', 'Foto do QPCA', true)}
                      {renderFotoField('foto_disjuntor_qpca', 'Foto do Disjuntor do QPCA', true)}
                      {renderFotoField('foto_aterramento_qpca', 'Foto do Aterramento do QPCA', true)}
                      {renderFotoField('foto_medicoes_strings', 'Foto das Medições das Strings', true, true, 'Você pode adicionar múltiplas imagens.')}
                      {renderFotoField('foto_planta_monitoramento', 'Foto da Planta de Monitoramento', !!baixaFormData.configuracao_inversor)}
                      {renderFotoField('foto_etiqueta_inversor', 'Foto da Etiqueta do Inversor', true)}
                      {renderFotoField('foto_datalogger', 'Foto do Datalogger', true)}

                      {/* Fotos Condicionais do QPCA - Bifásico */}
                      {baixaFormData.tipo_inversor === 'Bifásico' && (
                        <>
                          <div className="col-12 mt-3">
                            <h6 className="text-muted small">Medições do QPCA (Bifásico)</h6>
                          </div>
                          {renderFotoField('foto_fase1_terra_qpca', 'Fase 1 e Terra (QPCA)', true)}
                          {renderFotoField('foto_fase2_terra_qpca', 'Fase 2 e Terra (QPCA)', true)}
                          {renderFotoField('foto_fase1_fase2_qpca', 'Fase 1 e Fase 2 (QPCA)', true)}
                        </>
                      )}

                      {/* Fotos Condicionais do QPCA - Trifásico */}
                      {baixaFormData.tipo_inversor === 'Trifásico' && (
                        <>
                          <div className="col-12 mt-3">
                            <h6 className="text-muted small">Medições do QPCA (Trifásico)</h6>
                          </div>
                          {renderFotoField('foto_fase1_terra_qpca', 'Fase 1 e Terra (QPCA)', true)}
                          {renderFotoField('foto_fase2_terra_qpca', 'Fase 2 e Terra (QPCA)', true)}
                          {renderFotoField('foto_fase3_terra_qpca', 'Fase 3 e Terra (QPCA)', true)}
                          {renderFotoField('foto_fase1_fase2_qpca', 'Fase 1 e Fase 2 (QPCA)', true)}
                          {renderFotoField('foto_fase2_fase3_qpca', 'Fase 2 e Fase 3 (QPCA)', true)}
                          {renderFotoField('foto_fase3_fase1_qpca', 'Fase 3 e Fase 1 (QPCA)', true)}
                        </>
                      )}

                      {/* Seção: Fotos dos Módulos */}
                      <div className="col-12 mt-4">
                        <h6 className="border-bottom pb-2 mb-3">☀️ Fotos dos Módulos</h6>
                      </div>
                      {renderFotoField('foto_antes_modulos', 'Foto Antes de Instalar os Módulos', true)}
                      {renderFotoField('foto_apos_modulos', 'Foto Após Instalar os Módulos', true)}
                      {renderFotoField('foto_strings', 'Foto das Strings', true, true, 'Você pode adicionar múltiplas imagens.')}
                      {renderFotoField('foto_aterramento_modulos', 'Foto do Aterramento dos Módulos', false, false, 'Opcional')}
                      {renderFotoField('foto_aterramento_trilhos', 'Foto do Aterramento dos Trilhos', false, false, 'Opcional')}

                      </>
                      ) : (
                      /* Formulário simplificado (foto antes/depois) para os demais tipos: Manutenção, Corretiva, Troca de Inversor, Obra Cívil, etc. */
                      <>
                      <div className="col-12">
                        <h6 className="border-bottom pb-2 mb-3">📸 Fotos do Serviço (Antes e Depois)</h6>
                        <p className="text-muted small">Adicione fotos antes e após a realização do serviço.</p>
                      </div>
                      {renderFotoField('foto_antes_manutencao_correcao', 'Foto Antes do Serviço', true, true, 'Você pode adicionar múltiplas imagens.')}
                      {renderFotoField('foto_apos_manutencao_correcao', 'Foto Após o Serviço', true, true, 'Você pode adicionar múltiplas imagens.')}
                      {tipoExigeQuantidadeModulos(ordemParaBaixa?.tipo_servico?.nome) && (
                        <div className="col-md-6">
                          <label htmlFor="quantidade_modulos_simples" className="form-label">Qtd de Módulos</label>
                          <input
                            type="number"
                            id="quantidade_modulos_simples"
                            className="form-control"
                            min="0"
                            step="1"
                            value={baixaFormData.quantidade_modulos}
                            onChange={(e) => setBaixaFormData({...baixaFormData, quantidade_modulos: e.target.value})}
                            placeholder="0"
                          />
                        </div>
                      )}
                      </>
                      )}

                      {/* Valor da Obra - comum a todos os tipos */}
                      <div className="col-md-6">
                        <label htmlFor="valor_obra" className="form-label">Valor Obra</label>
                        <div className="input-group">
                          <span className="input-group-text">R$</span>
                          <input
                            type="text"
                            id="valor_obra"
                            className="form-control"
                            placeholder="0,00"
                            inputMode="numeric"
                            value={baixaFormData.valor_obra}
                            onChange={(e) => {
                              setBaixaFormData({...baixaFormData, valor_obra: aplicarMascaraValor(e.target.value)})
                            }}
                          />
                        </div>
                      </div>

                      {/* Observações - comum a todos os tipos */}
                      <div className="col-12">
                        <label htmlFor="observacoes" className="form-label">Observações</label>
                        <textarea
                          id="observacoes"
                          className="form-control"
                          rows="4"
                          value={baixaFormData.observacoes}
                          onChange={(e) => setBaixaFormData({...baixaFormData, observacoes: e.target.value})}
                          placeholder="Digite observações sobre a baixa..."
                        />
                      </div>
                    </div>
                    )}
                  </div>
                  <div className="modal-footer modal-footer-baixa" style={{ 
                    display: 'flex', 
                    justifyContent: 'flex-end', 
                    gap: '0.5rem',
                    padding: '1rem',
                    borderTop: '1px solid #dee2e6'
                  }}>
                    <button 
                      type="button" 
                      className="btn btn-secondary" 
                      onClick={handleCloseBaixaModal}
                      disabled={isLoadingBaixa || isLoadingBaixaParaEdicao}
                    >
                      Cancelar
                    </button>
                    <button 
                      type="submit" 
                      className="btn btn-primary" 
                      disabled={isLoadingBaixa || isLoadingBaixaParaEdicao}
                    >
                      {isLoadingBaixa ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Salvando...
                        </>
                      ) : (
                        '💾 Salvar Baixa'
                      )}
                    </button>
                  </div>
                  </>
                </form>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de Visualização de Baixa */}
      {showVisualizarBaixaModal && ordemParaVisualizar && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="modal-backdrop fade show" 
            onClick={handleCloseVisualizarBaixa}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1050
            }}
          />
          
          {/* Modal */}
          <div 
            className="modal fade show" 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1060,
              backgroundColor: 'transparent'
            }}
            onClick={handleCloseVisualizarBaixa}
          >
            <div 
              className="modal-dialog modal-dialog-centered modal-lg"
              style={{ maxWidth: '900px', margin: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content" style={{ maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                <div className="modal-header" style={{ borderBottom: '1px solid #dee2e6', padding: '1rem' }}>
                  <h5 className="modal-title">
                    📋 Informações de Baixa - {ordemParaVisualizar.cliente?.nome_completo || 'N/A'}
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={handleCloseVisualizarBaixa}
                    aria-label="Fechar"
                  />
                </div>
                
                <div className="modal-body" style={{ overflowY: 'auto', flex: '1 1 auto', padding: '1.5rem' }}>
                  {isLoadingVisualizarBaixa ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                      <p className="mt-3 text-muted">Carregando informações de baixa...</p>
                    </div>
                  ) : dadosBaixa ? (
                    <div className="row g-3">
                      {/* Informações Básicas */}
                      <div className="col-12">
                        <h6 className="border-bottom pb-2 mb-3">📋 Informações Básicas</h6>
                      </div>
                      
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Tipo de Padrão</label>
                        <p>{dadosBaixa.tipo_padrao_nome || 'N/A'}</p>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-bold">Tipo de Fase do Padrão</label>
                        <p className="text-capitalize">{dadosBaixa.tipo_fase || 'N/A'}</p>
                      </div>

                      {dadosBaixa.quantidade_modulos && (
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Qtd Módulos Instalados</label>
                          <p>{dadosBaixa.quantidade_modulos}</p>
                        </div>
                      )}
                      
                      {/* Informações Adicionais */}
                      <div className="col-12 mt-4">
                        <h6 className="border-bottom pb-2 mb-3">💰 Informações Adicionais</h6>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-bold">Configuração de Inversor</label>
                        <p>
                          {dadosBaixa.configuracao_inversor ? (
                            <span className="badge bg-success">✓ Sim</span>
                          ) : (
                            <span className="badge bg-secondary">✗ Não</span>
                          )}
                        </p>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-bold">Nota de Material</label>
                        <p>
                          {dadosBaixa.nota_material ? (
                            <span className="badge bg-success">✓ Sim</span>
                          ) : (
                            <span className="badge bg-secondary">✗ Não</span>
                          )}
                        </p>
                      </div>

                      {dadosBaixa.nota_material && dadosBaixa.valor_material && (
                        <div className="col-12">
                          <label className="form-label fw-bold">Valor do Material</label>
                          <p>
                            R$ {dadosBaixa.valor_material.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </p>
                        </div>
                      )}

                      {dadosBaixa.valor_obra != null && dadosBaixa.valor_obra !== '' && (
                        <div className="col-12">
                          <label className="form-label fw-bold">Valor Obra</label>
                          <p>
                            R$ {Number(dadosBaixa.valor_obra).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })}
                          </p>
                        </div>
                      )}

                      {dadosBaixa.observacao && (
                        <div className="col-12">
                          <label className="form-label fw-bold">Observações</label>
                          <p>{dadosBaixa.observacao}</p>
                        </div>
                      )}
                      
                      {/* Fotos */}
                      {dadosBaixa.fotos && typeof dadosBaixa.fotos === 'object' && (
                        <>
                          <div className="col-12 mt-4">
                            <h6 className="border-bottom pb-2 mb-3">📸 Fotos</h6>
                          </div>
                          
                          {Object.entries(dadosBaixa.fotos).map(([campo, urls]) => {
                            if (!urls || (Array.isArray(urls) && urls.length === 0)) return null
                            
                            const urlArray = Array.isArray(urls) ? urls : [urls]
                            const labelsFotos = {
                              foto_planta_monitoramento: 'Foto da Planta de Monitoramento',
                              foto_fachada: 'Foto da Fachada',
                              foto_padrao_completo: 'Foto do Padrão Completo',
                              foto_etiqueta_inversor: 'Foto da Etiqueta do Inversor',
                              foto_datalogger: 'Foto do Datalogger',
                              foto_instalacao_inversor: 'Foto da Instalação do Inversor',
                              foto_qpca: 'Foto do QPCA',
                              foto_disjuntor_padrao: 'Foto do Disjuntor do Padrão',
                              foto_aterramento_padrao: 'Foto do Aterramento do Padrão',
                              foto_conexoes_padrao: 'Foto das Conexões do Padrão',
                              foto_disjuntor_qpca: 'Foto do Disjuntor do QPCA',
                              foto_aterramento_qpca: 'Foto do Aterramento do QPCA',
                              foto_medicoes_strings: 'Foto das Medições das Strings',
                              foto_antes_modulos: 'Foto Antes de Instalar os Módulos',
                              foto_apos_modulos: 'Foto Após Instalar os Módulos',
                              foto_strings: 'Foto das Strings',
                              foto_antes_manutencao_correcao: 'Foto Antes do Serviço',
                              foto_apos_manutencao_correcao: 'Foto Após o Serviço'
                            }
                            const label = labelsFotos[campo] || campo.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                            
                            return (
                              <div key={campo} className="col-12">
                                <label className="form-label fw-bold">{label}</label>
                                <div className="row g-2">
                                  {urlArray.map((url, index) => (
                                    <div key={index} className="col-md-3 col-6">
                                      <img
                                        src={url}
                                        alt={`${label} ${index + 1}`}
                                        className="img-thumbnail"
                                        style={{ width: '100%', height: '150px', objectFit: 'cover', cursor: 'pointer' }}
                                        onClick={() => window.open(url, '_blank')}
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )
                          })}
                        </>
                      )}
                      
                      <div className="col-12 mt-3">
                        <label className="form-label fw-bold">Data de Criação</label>
                        <p>{dadosBaixa.created_at ? new Date(dadosBaixa.created_at).toLocaleString('pt-BR') : 'N/A'}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <p className="text-muted">Nenhuma informação de baixa encontrada para este cliente.</p>
                    </div>
                  )}
                </div>
                
                <div className="modal-footer" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  gap: '0.5rem',
                  padding: '1rem',
                  borderTop: '1px solid #dee2e6'
                }}>
                  {dadosBaixa && userRole === 'administrador' && !ordemParaVisualizar.medicao_fechada && (
                    <button 
                      type="button" 
                      className="btn btn-danger" 
                      onClick={handleOpenConfirmExcluirBaixa}
                      disabled={isExcluindoBaixa}
                    >
                      🗑️ Excluir Baixa
                    </button>
                  )}
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleCloseVisualizarBaixa}
                    disabled={isExcluindoBaixa}
                  >
                    Fechar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de Confirmação de Exclusão de Baixa */}
      {showConfirmExcluirBaixaModal && createPortal(
        <>
          {/* Backdrop */}
          <div 
            className="modal-backdrop fade show" 
            onClick={handleCloseConfirmExcluirBaixa}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1070
            }}
          />
          
          {/* Modal */}
          <div 
            className="modal fade show" 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1080,
              backgroundColor: 'transparent'
            }}
            onClick={handleCloseConfirmExcluirBaixa}
          >
            <div 
              className="modal-dialog modal-dialog-centered"
              style={{ maxWidth: '500px', margin: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content">
                <div className="modal-header border-0 pb-0">
                  <h5 className="modal-title text-danger">
                    ⚠️ Confirmar Exclusão
                  </h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={handleCloseConfirmExcluirBaixa}
                    aria-label="Fechar"
                    disabled={isExcluindoBaixa}
                  />
                </div>
                
                <div className="modal-body">
                  <p className="mb-3">
                    Tem certeza que deseja excluir esta baixa?
                  </p>
                  
                  <div className="alert alert-warning mb-0">
                    <strong>Esta ação irá:</strong>
                    <ul className="mb-0 mt-2">
                      <li>Excluir todas as fotos do bucket</li>
                      <li>Excluir o registro de baixa</li>
                      <li>Alterar o status da ordem para "Em andamento"</li>
                    </ul>
                    <p className="mb-0 mt-2 fw-bold text-danger">
                      ⚠️ Esta ação não pode ser desfeita!
                    </p>
                  </div>
                </div>
                
                <div className="modal-footer border-0" style={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  gap: '0.5rem',
                  padding: '1rem'
                }}>
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={handleCloseConfirmExcluirBaixa}
                    disabled={isExcluindoBaixa}
                  >
                    Cancelar
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger" 
                    onClick={handleExcluirBaixa}
                    disabled={isExcluindoBaixa}
                  >
                    {isExcluindoBaixa ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                        Excluindo...
                      </>
                    ) : (
                      <>
                        🗑️ Sim, Excluir
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}

      {/* Modal de PDFs anexados ao cliente da ordem */}
      {showPdfsModal && pdfsModalData && (
        <ClientePDFs
          clienteIdCadastro={pdfsModalData.id_cliente}
          clienteNome={pdfsModalData.nome_completo}
          onClose={() => {
            setShowPdfsModal(false)
            setPdfsModalData(null)
          }}
        />
      )}
    </div>
  )
}

export default GestaoInstalacao
